import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'

import OptimiserNode from '@/apps/glass-production-station/src/classes/optimiserNode'
import OptimiserSheet from './optimiserSheet'
import Rectangle from '@/model/rectangle'

import { OptimiserSheetStatusTypes, SheetAllocationTypes, SheetAllocationStatusTypes, OptimiserSheetNodeTypes, OrderStatusTypes, OrderTypes, StockGrainDirectionTypes, StockFaceTypes, OptimiserSheetTypes, ProductionPanelStatusTypes } from '@/store/types'
import { EdgeTypes, SegmentTypes } from '@/store/mathTypes'
import { OptimiserEditorSelectionTypes } from '@/apps/glass-production-station/src/classes/optimiserEditorTypes'
import OptimiserAllocation from './optimiserAllocation'



/**
 * Optimiser Sheet Editor
 * Used to edit the panels / offcuts in an optimised sheet
 */
export default class OptimiserSheetEditor {
  constructor(editorComponent, canvasElement, canvasContainerElement, productionStationId) {
    this.editorComponent = editorComponent
    this.canvasElement = canvasElement
    this.canvasContainerElement = canvasContainerElement
    this.productionStationId = productionStationId

    this.developerDisplayEnabled = false    // Developer / Debugging info display

    this.editingSheet = null
    this.editingSheetConfig = {}  // Sheet config/rules from the server
    this.moveSheetCopy = null

    this.pendingPanels = []       // Panels manually added in waiting for the server to alloc into the sheet layout
    this.pendingBlankPanels = []  // Blank panels added via editor & waiting for the server to alloc into the sheet layout 

    this.maxUndoHistory = 50
    this.currentUndoSheetSnapshot = null
    this.undoSheetList = []
    this.scratchPadSize = 300
    this.info = {
      optimiserSheetId: null,
      zoomStep: 0,
      scale: 1.0,
      originOffsetX: 0,     // Origin offset of the sheet. In screen coordinates
      originOffsetY: 0,
      sendingLayoutUpdate: false
    }

    this.highlightInfo = {
      type: OptimiserEditorSelectionTypes.none
    }
    this.selectedInfo = {
      type: OptimiserEditorSelectionTypes.none
    }

    this.panning = {
      active: false,
      posX: 0,
      posY: 0
    }

    this.selectionBox = {
      active: false,
      mouseDown: false,
      startMouseX: 0,
      startMouseY: 0,
      currentMouseX: 0,
      currentMouseY: 0
    }

    this.grabbedAlloc = {
      grabbed: false,
      mouseDown: false,
      cancelled: false,
      canMove: false,
      allocId: null,
      clickX: 0,
      clickY: 0,
      currentSheetCoordX: 0,
      currentSheetCoordY: 0,
      grabOffsetX: 0,
      grabOffsetY: 0,
      grabAngle: 0,
      sourceWidth: 0,
      sourceHeight: 0,
      grainDirection: '',
      stockFaceSideUpType: '',
      flippedHorz: false,
      flippedVert: false,

      // New panel dragged from panel schedule / scratchpad
      isNewPanel: false,
      isPanelScratchPad: false,
      productionPanelId: null,
      stockId: null,
      panelDownloading: false,
      panelDownloadFailed: false,
      panelDownloadOrderItemInfo: null

    }

    this.settings = {
      zoom: {
        minZoom: 0.10,
        maxZoom: 10,
        zoomStepCount: 100,
        wheelZoomFactor: 120
      },

      backgroundColour: '#141414',
      //sheetBaseColour: '#e78b92',
      sheetBaseColour: '#808080',
      panelColour: '#b8e7f9',
      scratchPadPanelColour: 'rgba(255, 255, 255, 0.4)',
      offcutColour: '#c9f6d5',
      defectColour: '#8080FF',
      blankPanelColour: '#E6E6A0',
      splitPanelColour: '#AD7DFA',
      completedPanelColour: '#9DBDE3',
      completedOffcutColour: '#9DCCB7',
      overriddenPanelStrokeStyle: 'rgba(7, 153, 146, 0.9)',
      borderStrokeStyle: 'rgba(0,0,0,1)',
      highlightStrokeStyle: 'rgba(255,0,0,0.9)',
      highlightEdgeStrokeStyle: 'rgba(64,64,255,0.75)',
      highlightCuttingLineStrokeStyle: 'rgba(32,200,32,0.9)',
      highlightCuttingLineNearestPointFillStyle: 'rgba(32,200,32,0.9)',
      highlightReliefCutStrokeStyle: 'rgba(255,32,255,0.9)',
      highlightCutoutStrokeStyle: 'rgba(16,200,200,0.95)',
      selectedStrokeStyle: 'rgba(255,255,0,0.9)',
      selectedFillStyle: 'rgba(255,255,0,0.1)',
      reliefCutStrokeStyle: 'rgba(128,0,128,0.9)',
      cutoutCutStokeStyle: 'rgba(0,200,128,0.9)',
      draggingStrokeStyle: 'rgba(0,0,0,0.5)',
      draggingSuccessFillStyle: 'rgba(0,255,0,0.15)',
      draggingSuccessStrokeStyle: 'rgba(0,255,0,0.75)',
      draggingFailStrokeStyle: 'rgba(255,0,0,0.75)',
      draggingFailFillStyle: 'rgba(255,0,0,0.25)',
      draggingScratchFillStyle: 'rgba(255,255,255,0.15)',
      draggingScratchStrokeStyle: 'rgba(255,255,255,0.75)',
      selectionBoxStrokeStyle: 'rgba(64, 64, 64, 0.75)',
      selectionBoxFillStyle: 'rgba(200, 200, 255, 0.20)',
      panelShapeStrokeStyle: 'rgba(0,0,0,0.2)',
      panelShapeFillStyle: 'rgba(0,0,0,0.075)',
      panelHoleFillStyle: '#b8e7f9',
      panelTrimFillStyle: '#D2EFFA',
      panelSquareCornerStrokeStyle: 'rgba(0,0,0,0.3)',
      straightLineCutStrokeStyle: 'rgba(0,0,0,1)',
      shapedCutStrokeStyle: 'rgba(0,0,0,1)',
      textFillStyle: '#000000'
    }


    this.info.zoomStep = this.calculateZoomStepFromScale(this.info.scale);
  }


  /**
   * Clear the editor settings & reset to defaults
   * Done when sheet removed
   */
  clearSheet() {
    this.clearUndoList()

    this.pendingPanels = []
    this.pendingBlankPanels = []

    this.grabbedAlloc.grabbed = false
    this.grabbedAlloc.mouseDown = false
    this.grabbedAlloc.cancelled = false

    this.selectionBox.active = false
    this.selectionBox.mouseDown = false

    this.info.optimiserSheetId = null
    this.info.scale = 1
    this.info.sendingLayoutUpdate = false

    this.editingSheetConfig = {}
  }


  /**
   * Update the editor with an updated OptimiserSheet object from the server
   * Called when layout changes
   */
  updateSheet(sheet, options = {}) {
    let sheetData = _.cloneDeep(sheet)

    // Map some properties from nested objects
    sheetData.isStockUsingGrain = false
    sheetData.isStockUsingFaces = false

    if(sheetData.stock) {
      sheetData.isStockUsingGrain = sheetData.stock.isStockUsingGrain
      sheetData.isStockUsingFaces = sheetData.stock.isStockUsingFaces
      sheetData.defaultFaceSideUp = sheetData.stock.cuttingFaceSideUp
    }
    
    let allocData = JSON.parse(sheet.sheetAllocationData)
    this.editingSheet = new OptimiserSheet(sheetData, allocData.rootNode)


    // If sheet changed, clear undo
    if(this.info.optimiserSheetId != this.editingSheet.id) {
      this.clearUndoList()
    }

    // Clear pending list
    this.pendingPanels = []
    this.pendingBlankPanels = []

    // Update info
    this.info.optimiserSheetId = this.editingSheet.id
    if(options.resetViewport)
      this.resetZoom()

    this.info.sendingLayoutUpdate = false
    this.render()
  }


  /**
   * Update the sheet config / rules info from the server
   */
  updateSheetConfig(sheetConfig) {
    this.editingSheetConfig = sheetConfig
  }


  /**
   * Reload the sheet from the server
   */
  reloadSheetFromServer() {
    if(!this.editingSheet.id)
      return

    this.editorComponent.updatingServer = true

    this.editorComponent.$apiService.glassProductionStationService.getOptimiserSheet(this.editingSheet.id)
    .then(response => {
      this.editorComponent.updatingServer = false

      this.editorComponent.optimiserSheet = response.data
      this.editorComponent.$emit('sheetUpdated', response.data)

      this.updateSheet(response.data, { resetViewport: true })
      this.editorComponent.sheetUpdated()
    })
    .catch(err => {
      console.error(err)

      this.editorComponent.updatingServer = false
      this.editorComponent.displayNetworkError(err, 'Failed to reload sheet')
    })

  }


  /**
   * Send the sheet info for updating / auto-filling to the server
   */
  sendSheetLayoutUpdate(options = {}) {
    let defectList = this.getDefectList()
    let pendingPanelList = this.getPendingPanelList()
    let pendingBlankPanelList = this.getPendingBlankPanelList()
    let lockedPanelList = this.getLockedPanelList()
    let lockedBlankPanelList = this.getLockedBlankPanelList()
    let prePackingData = this.buildPrePackingData()

    let panelConfigList = this.buildPanelConfigList()

    this.clearPendingList()

    // TODO: Maybe assign a higher priority to pending panels
    let panels = _.uniqBy(_.concat(pendingPanelList, lockedPanelList), 'id')
    let blankPanels = _.concat(pendingBlankPanelList, lockedBlankPanelList)

    // Clear all panels if requested
    if(options.clearAllPanels) {
      panels = []
      prePackingData = {}
      panelConfigList = []
      blankPanels = []
    }

    // Clear all existing packing data if requested
    // Leaves config list as this contains allocation overrides
    if(options.clearAllPanelPackingData) {
      prePackingData = {}
    }

    // Clear unlocked panels if requested
    if(options.clearUnlockedPanels) {
      panels = _.uniqBy(_.concat(pendingPanelList, lockedPanelList), 'id')
    }

    let sheetConfig = {
      isRotated: this.editingSheet.isRotated
    }
    
    let updateData = {
      sheetConfig: sheetConfig,
      panels: panels,
      blankPanels: blankPanels,
      panelConfigs: panelConfigList,
      defects: defectList,
      prePackingData: prePackingData,
      autoFillPanels: !!options.autoFillPanels
    }

    this.info.sendingLayoutUpdate = true
    this.editorComponent.updatingServer = true
    this.editorComponent.$apiService.glassProductionStationService.updateSheetLayout(this.productionStationId, this.editingSheet.id, updateData)
    .then(response => {
      this.editorComponent.optimiserSheet = response.data
      this.editorComponent.$emit('sheetUpdated', response.data)

      this.updateSheet(response.data, { resetViewport: options.resetViewport })
      this.editorComponent.sheetUpdated()

      this.editorComponent.updatingServer = false
      this.info.sendingLayoutUpdate = false
    })
    .catch(err => {
      console.error(err)

      this.info.sendingLayoutUpdate = false
      this.editorComponent.updatingServer = false
      this.editorComponent.displayNetworkError(err, 'Failed to update sheet')

      // Revert the sheet to server version
      // Only if 4xx or 5xx status. Ignore for no-internet
      if(err.response && err.response.status >= 400) {
        this.reloadSheetFromServer()
      }
    })
  }


  /**
   * A panel was dragged into the editor from the panel list
   * Move straight into allocation-dragged process
   * Since no allocation exists for the panel, we create a temporary one to be setup by the server when we release the panel
   */
  panelDraggedToEditor(panelData) {
    if(!this.isSheetEditingAvailable())
      return

    this.grabbedAlloc.grabbed = true
    this.grabbedAlloc.mouseDown = true
    this.grabbedAlloc.cancelled = false
    this.grabbedAlloc.canMove = true
    this.grabbedAlloc.allocId = null
    this.grabbedAlloc.clickX = 0
    this.grabbedAlloc.clickY = 0
    this.grabbedAlloc.currentSheetCoordX = 0
    this.grabbedAlloc.currentSheetCoordY = 0
    this.grabbedAlloc.grabOffsetX = panelData.width / 2
    this.grabbedAlloc.grabOffsetY = panelData.height / 2
    this.grabbedAlloc.grabAngle = 0
    this.grabbedAlloc.sourceWidth = panelData.width
    this.grabbedAlloc.sourceHeight = panelData.height
    this.grabbedAlloc.flippedHorz = false
    this.grabbedAlloc.flippedVert = false

    this.grabbedAlloc.isNewPanel = true
    this.grabbedAlloc.isPanelScratchPad = false
    this.grabbedAlloc.productionPanelId = panelData.panelId
    this.grabbedAlloc.stockId = panelData.stockId
    this.grabbedAlloc.grainDirection = panelData.grainDirection
    this.grabbedAlloc.stockFaceSideUpType = panelData.stockFaceSideUpType

    // If sheet has grain direction, rotate the panel to match the grain
    if(this.editingSheet.isStockUsingGrain && this.grabbedAlloc.grainDirection) {
      if(this.editingSheet.grainDirection != StockGrainDirectionTypes.none && this.grabbedAlloc.grainDirection != StockGrainDirectionTypes.none) {

        if(this.editingSheet.grainDirection != this.grabbedAlloc.grainDirection) {
          this.grabbedAlloc.grabAngle = 90

          this.grabbedAlloc.grabOffsetX = panelData.height / 2
          this.grabbedAlloc.grabOffsetY = panelData.width / 2
        }
      }
    }

    // Side face flippage
    if(this.editingSheet.isStockUsingFaces && this.grabbedAlloc.stockFaceSideUpType) {
      // Sheets on the optimiser are always smooth side up
      // Adjust the panel to match. Only rotate on horizontal axis
      if(this.grabbedAlloc.stockFaceSideUpType != this.editingSheet.defaultFaceSideUp)
        this.grabbedAlloc.flippedHorz = true
    }

    // Clear selection box
    this.selectionBox.active = false
    this.selectionBox.mouseDown = false

    // Download the data from the server so we know how much trim & info to apply
    this.grabbedAlloc.panelDownloading = true
    this.requestPanelOptimiserDetails(panelData.panelId)
  }


  /**
   * On Mouse Wheel Event
   */
  onMouseWheelEvent(ev) {
    // Logarithmic zooming
    this.info.zoomStep += ev.wheelDeltaY / this.settings.zoom.wheelZoomFactor
    this.info.zoomStep = Math.max(this.info.zoomStep, 0)
    this.info.zoomStep = Math.min(this.info.zoomStep, this.settings.zoom.zoomStepCount)

    let logZoom = Math.log(this.settings.zoom.minZoom) + (Math.log(this.settings.zoom.maxZoom)- Math.log(this.settings.zoom.minZoom))*this.info.zoomStep/(this.settings.zoom.zoomStepCount-1)

    // Move camera towards mouse
    let moveMultiplier = ((Math.exp(logZoom) - this.info.scale) / this.info.scale)
    this.info.originOffsetX -= (ev.offsetX - this.info.originOffsetX) * moveMultiplier
    this.info.originOffsetY -= (ev.offsetY - this.info.originOffsetY) * moveMultiplier

    this.info.scale = Math.exp(logZoom)

    this.clampCameraPosition()
    this.render()

    return false
  }


  /**
   * On Mouse Down Event
   */
  onMouseDownEvent(ev) {
    // Panning? Right click to start panning
    if(ev.buttons & 2) {
      this.panning.active = true
      this.panning.posX = ev.offsetX
      this.panning.posY = ev.offsetY
      return
    }

    // TODO: If panel download in progress, ignore this?
    this.clearGrabbed()

    this.selectionBox.active = false
    this.selectionBox.mouseDown = false
    this.selectionBox.startMouseX = ev.offsetX
    this.selectionBox.startMouseY = ev.offsetY

    let sheetCoordX = (ev.offsetX - this.info.originOffsetX) / this.getScale()
    let sheetCoordY = (ev.offsetY - this.info.originOffsetY) / this.getScale()

    let sheetAlloc = this.editingSheet.getSheetAllocationAtPosition(sheetCoordX, sheetCoordY)
    if(sheetAlloc) {
      this.grabbedAlloc.allocType = sheetAlloc.type
      this.grabbedAlloc.allocId = sheetAlloc.id
      this.grabbedAlloc.mouseDown = true
      this.grabbedAlloc.canMove = false
      this.grabbedAlloc.clickX = ev.offsetX
      this.grabbedAlloc.clickY = ev.offsetY
      this.grabbedAlloc.currentSheetCoordX = sheetCoordX
      this.grabbedAlloc.currentSheetCoordY = sheetCoordY
      this.grabbedAlloc.grabOffsetX = sheetCoordX - sheetAlloc.x
      this.grabbedAlloc.grabOffsetY = sheetCoordY - sheetAlloc.y
      this.grabbedAlloc.grabAngle = sheetAlloc.angle
      this.grabbedAlloc.sourceWidth = sheetAlloc.width
      this.grabbedAlloc.sourceHeight = sheetAlloc.height
      this.grabbedAlloc.isPanelScratchPad = (sheetAlloc.type == SheetAllocationTypes.panelScratchPad)
      this.grabbedAlloc.grainDirection = sheetAlloc.grainDirection
      this.grabbedAlloc.flippedHorz = sheetAlloc.flippedHorz
      this.grabbedAlloc.flippedVert = sheetAlloc.flippedVert

      if(sheetAlloc.type == SheetAllocationTypes.panel || sheetAlloc.type == SheetAllocationTypes.panelScratchPad || sheetAlloc.type == SheetAllocationTypes.blankPanel) {
        this.grabbedAlloc.sourceWidth = sheetAlloc.sourceWidth
        this.grabbedAlloc.sourceHeight = sheetAlloc.sourceHeight
        this.grabbedAlloc.canMove = true

        if(!this.isSheetEditingAvailable())
          this.grabbedAlloc.canMove = false
      }
    }


    // Selection box
    // Ignore if started over empty / offcuts
    if(sheetAlloc && (sheetAlloc.type == SheetAllocationTypes.panel || sheetAlloc.type == SheetAllocationTypes.panelScratchPad || sheetAlloc.type == SheetAllocationTypes.defect || sheetAlloc.type == SheetAllocationTypes.blankPanel))
      return

    this.selectionBox.mouseDown = true
  }


  /**
   * On Mouse Move Event
   */
  onMouseMoveEvent(ev) {
    let mouseCursor = 'default'
    let sheetCoordX = (ev.offsetX - this.info.originOffsetX) / this.getScale()
    let sheetCoordY = (ev.offsetY - this.info.originOffsetY) / this.getScale()

    if(this.panning.active) {
      let dirX = this.panning.posX - ev.offsetX
      let dirY = this.panning.posY - ev.offsetY
      this.panning.posX = ev.offsetX
      this.panning.posY = ev.offsetY

      // Move the camera
      this.info.originOffsetX -= dirX
      this.info.originOffsetY -= dirY

      // Clamp camera to world limits
      this.clampCameraPosition()
      this.render()
      this.canvasElement.style.cursor = mouseCursor

      return false
    }

    // Grabbed an allocation
    if(this.grabbedAlloc.mouseDown && this.grabbedAlloc.canMove && !this.grabbedAlloc.grabbed) {
      let moveThreshold = 5

      if(Math.abs(this.grabbedAlloc.clickX - ev.offsetX) >= moveThreshold || Math.abs(this.grabbedAlloc.clickY - ev.offsetY) >= moveThreshold) {
        this.grabbedAlloc.grabbed = true

        // Clear highlight/selection info
        this.clearSelectedInfo()
        this.clearHighlightInfo()
      }
    }
    
    if(this.grabbedAlloc.grabbed) {
      this.grabbedAlloc.currentSheetCoordX = sheetCoordX
      this.grabbedAlloc.currentSheetCoordY = sheetCoordY

      // Display move allocation stuff & temporary sheet tree with movements
      this.renderMoveGrabbedAllocation()

      this.canvasElement.style.cursor = 'pointer'
      return true
    }

    // Selection box
    if(this.selectionBox.mouseDown) {
      let selectionThreshold = 5

      if(!this.selectionBox.active) {
        if(Math.abs(this.selectionBox.startMouseX - ev.offsetX) >= selectionThreshold || Math.abs(this.selectionBox.startMouseY - ev.offsetY) >= selectionThreshold) {
          this.selectionBox.active = true
        }
      }

      if(this.selectionBox.active) {
        this.selectionBox.currentMouseX = ev.offsetX
        this.selectionBox.currentMouseY = ev.offsetY
      }
    }

    // Mouse over highlights
    this.clearHighlightInfo()

    this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
      alloc.highlighted = false
    })
    if(!this.selectionBox.active) {
      this.highlightInfo = this.getSelectionInfoAtPosition(sheetCoordX, sheetCoordY)

      if(this.highlightInfo.type != OptimiserEditorSelectionTypes.none)
        mouseCursor = 'pointer'

      switch(this.highlightInfo.type) {
        case OptimiserEditorSelectionTypes.allocation:
        {
          let alloc = this.editingSheet.findAllocationById(this.highlightInfo.allocationId)
          alloc.highlighted = true
        }
        break
      }
    }
    
    this.render()
    this.canvasElement.style.cursor = mouseCursor

    return false
  }


  /**
   * On Mouse Up Event
   */
  onMouseUpEvent(ev) {
    if(this.panning.active) {
      this.panning.active = false
      return false
    }

    let handled = this.grabbedAlloc.grabbed || this.grabbedAlloc.cancelled

    // Output node info
    let sheetCoordX = (ev.offsetX - this.info.originOffsetX) / this.getScale()
    let sheetCoordY = (ev.offsetY - this.info.originOffsetY) / this.getScale()

    // Output node info
    if(this.developerDisplayEnabled) {
      let debugNode = this.editingSheet.findNodeAtPosition(sheetCoordX, sheetCoordY)
      this.editingSheet.debugOutputNodeTree()
      console.log('%cClicked node:', 'font-weight: bold;font-size:14px')
      this.editingSheet._debugOutputNode(debugNode)
    }

    // Commit the results of the drag
    if(this.grabbedAlloc.grabbed) {
      // Run checks on the dragged panel to make sure it can be used in the sheet
      // Eg, stock id & no duplicates
      if(this.grabbedAlloc.isNewPanel) {
        // If download not finished or has an error, show message
        if(this.grabbedAlloc.panelDownloading || this.grabbedAlloc.panelDownloadFailed) {
          this.editorComponent.errorMessage('Network Error', 'Unable to access panel details from server. Please check connection and try again')

          this.clearGrabbed()
          this.render()
          return
        }

        if(this.grabbedAlloc.stockId != this.editingSheet.stockId) {
          this.editorComponent.errorMessage('Invalid Panel', 'The panel\'s stock does not match the sheet')

          this.clearGrabbed()
          this.render()
          return
        }

        if(this.editingSheet.findNodeByPanelId(this.grabbedAlloc.productionPanelId)) {
          this.editorComponent.errorMessage('Duplicate Panel', 'The panel already exists in the sheet')

          this.clearGrabbed()
          this.render()
          return
        }
      }

      this.beginSheetUndoSnapshot()
      this.commitUndoSnapshot()

      this.editingSheet = this.moveSheetCopy.clone()

      // Lock the grabbed allocation in place
      let movedAlloc = this.editingSheet.findAllocationById(this.grabbedAlloc.allocId)
      if(movedAlloc)
        movedAlloc.isUserLocked = true

      // Send updated tree node list to server
      this.sendSheetLayoutUpdate()
    }

    // Selection box
    if(this.selectionBox.mouseDown && this.selectionBox.active) {
      // Select allocations
      let selX = (Math.min(this.selectionBox.startMouseX, this.selectionBox.currentMouseX) - this.info.originOffsetX) / this.getScale()
      let selY = (Math.min(this.selectionBox.startMouseY, this.selectionBox.currentMouseY) - this.info.originOffsetY) / this.getScale()
      let selWidth = Math.abs(this.selectionBox.currentMouseX - this.selectionBox.startMouseX) / this.getScale()
      let selHeight = Math.abs(this.selectionBox.currentMouseY - this.selectionBox.startMouseY) / this.getScale()

      this.clearSelectedInfo()
      this.clearSelectedAllocation()

      this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
        if(alloc.isRectangleIntersection(selX, selY, selWidth, selHeight))
          alloc.selected = true
        else
          alloc.selected = false
      })
    }

    // Single click
    if(!this.selectionBox.active && !this.grabbedAlloc.grabbed) {
      this.selectedInfo = this.getSelectionInfoAtPosition(sheetCoordX, sheetCoordY)

      this.clearSelectedAllocation()

      // Check if clicking on an edge
      switch(this.selectedInfo.type) {
         case OptimiserEditorSelectionTypes.nodeEdge:
          {
            // Always extenable if edge selected now
            this.selectedInfo.selectedEdgeExtendable = true
          }
          break

          case OptimiserEditorSelectionTypes.allocation:
          {
            let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
            alloc.selected = true
          }
          break

          case OptimiserEditorSelectionTypes.cuttingLine:
          case OptimiserEditorSelectionTypes.reliefCut:
          case OptimiserEditorSelectionTypes.cutout:
            break

          default:
            handled = true
      }
    }

    this.selectionBox.mouseDown = false
    this.selectionBox.active = false

    this.render()

    this.clearGrabbed()

    return handled
  }


  /**
   * Double Click event
   */
  onDoubleClickEvent(ev) {
    let sheetCoordX = (ev.offsetX - this.info.originOffsetX) / this.getScale()
    let sheetCoordY = (ev.offsetY - this.info.originOffsetY) / this.getScale()

    let sheetAlloc = this.editingSheet.getSheetAllocationAtPosition(sheetCoordX, sheetCoordY)

    // Zoom into the allocation
    if(sheetAlloc)
      this.zoomToAllocation(sheetAlloc)
  }


  /**
   * Clear grabbed state
   */
  clearGrabbed() {
    this.grabbedAlloc.grabbed = false
    this.grabbedAlloc.mouseDown = false
    this.grabbedAlloc.isNewPanel = false
    this.grabbedAlloc.isPanelScratchPad = false
    this.grabbedAlloc.cancelled = false

    this.grabbedAlloc.productionPanelId = null
    this.grabbedAlloc.panelDownloading = false
    this.grabbedAlloc.panelDownloadFailed = false
    this.grabbedAlloc.panelDownloadInfo = null
  }


  /**
   * On KeyDown event
   */
  onKeyDownEvent(ev) {
    let KeyCodeTab = 9
    let KeyCodeEscape = 27
    let KeyCodeDelete = 46
    let KeyCodeBackspace = 8

     // Ctrl / Cmd actions
     if (ev.metaKey || ev.ctrlKey) {        
      switch(ev.key) {
        // Cmd-Z / Ctrl-Z : Undo last action
        case 'z':
          if(!this.isSendingLayoutUpdate())
            this.undoLastAction()
          return
      }
    }

    // Check for keys while grabbed an allocation
    // Tab - Rotate Clockwise
    // Escape - Cancel grab
    if(this.grabbedAlloc.grabbed) {

      if(ev.keyCode == KeyCodeTab) {
        // Check for grain & don't allow rotate by 90 if not possible for the stock
        let rotateAngle = 90
        if(this.editingSheet.isStockUsingGrain)
          rotateAngle = 180

        this.grabbedAlloc.grabAngle = MathsUtils.wrapAngleDegrees(this.grabbedAlloc.grabAngle + rotateAngle)

        // Adjust grab position to center of the rotated rect
        let targetWidth = this.grabbedAlloc.sourceWidth
        let targetHeight = this.grabbedAlloc.sourceHeight

        // Rotate
        if(this.grabbedAlloc.grabAngle == 90 || this.grabbedAlloc.grabAngle == 270) {
          targetWidth = this.grabbedAlloc.sourceHeight
          targetHeight = this.grabbedAlloc.sourceWidth
        }

        this.grabbedAlloc.grabOffsetX = targetWidth/2
        this.grabbedAlloc.grabOffsetY = targetHeight/2

        this.renderMoveGrabbedAllocation()
      }

      // Escape - Cancels the move
      if(ev.keyCode == KeyCodeEscape) {
        this.grabbedAlloc.grabbed = false
        this.grabbedAlloc.mouseDown = false
        this.grabbedAlloc.cancelled = true

        this.render()
      }
    }

    // Delete / Backspace
    // Remove selected allocations
    if(this.isSheetEditingAvailable()) {
      if(ev.keyCode == KeyCodeDelete || ev.keyCode == KeyCodeBackspace) {
        this.removeSheetSelectedAllocation()
        this.editorComponent.hidePopovers()
      }
    }
  }


  /**
   * Render the move allocation process when an allocation is grabbed & moved
   */
  renderMoveGrabbedAllocation() {
    this.moveSheetCopy = this.editingSheet.clone()

    let moveAlloc = null
    let sheetCoordX = this.grabbedAlloc.currentSheetCoordX
    let sheetCoordY = this.grabbedAlloc.currentSheetCoordY

    let targetX = sheetCoordX - this.grabbedAlloc.grabOffsetX
    let targetY = sheetCoordY - this.grabbedAlloc.grabOffsetY

    targetX = Math.round(targetX)
    targetY = Math.round(targetY)

    let targetWidth = this.grabbedAlloc.sourceWidth
    let targetHeight = this.grabbedAlloc.sourceHeight

    // Rotate
    if(this.grabbedAlloc.grabAngle == 90 || this.grabbedAlloc.grabAngle == 270) {
      targetWidth = this.grabbedAlloc.sourceHeight
      targetHeight = this.grabbedAlloc.sourceWidth
    }

    let insertResult = false
    let scratchPadInsert = false

    // Check if over the scratchpad area
    if(sheetCoordX > this.moveSheetCopy.width && (this.grabbedAlloc.allocType == SheetAllocationTypes.panel || this.grabbedAlloc.allocType == SheetAllocationTypes.panelScratchPad)) {
      scratchPadInsert = true

      // Remove the node + allocation from the sheet
      if(!this.grabbedAlloc.isNewPanel) {
        moveAlloc = this.moveSheetCopy.findAllocationById(this.grabbedAlloc.allocId)
        if(moveAlloc) {
          // Move to scratch pad
          this.moveSheetCopy.moveAllocationToScratchPad(moveAlloc, targetX, targetY, targetWidth, targetHeight, this.grabbedAlloc.grabAngle)
        }
      } else {
        // Insert straight to scratch pad
        this.moveSheetCopy.insertPanelToScratchPad(this.grabbedAlloc.productionPanelId, targetX, targetY, targetWidth, targetHeight, this.grabbedAlloc.grabAngle)
      }
    }

    if(!scratchPadInsert) {
      if(!this.grabbedAlloc.isNewPanel && !this.grabbedAlloc.isPanelScratchPad) {
        moveAlloc = this.moveSheetCopy.findAllocationById(this.grabbedAlloc.allocId)
        if(!moveAlloc)
          return

        insertResult = this.moveSheetCopy.resizeNodePositionInTree(moveAlloc, moveAlloc.packingNodeId, targetX, targetY, targetWidth, targetHeight, this.grabbedAlloc.grabAngle, { x: sheetCoordX, y: sheetCoordY })
      
      } else if(this.grabbedAlloc.isPanelScratchPad) {
        moveAlloc = this.moveSheetCopy.findAllocationById(this.grabbedAlloc.allocId)
        if(!moveAlloc)
          return

        insertResult = this.moveSheetCopy.resizeNodePositionInTree(moveAlloc, null, targetX, targetY, targetWidth, targetHeight, this.grabbedAlloc.grabAngle, { x: sheetCoordX, y: sheetCoordY })

      } else {
        moveAlloc = {
          x: targetX,
          y: targetY,
          width: targetWidth,
          height: targetHeight
        }

        insertResult = this.moveSheetCopy.insertPanelInTree(this.grabbedAlloc.productionPanelId, targetX, targetY, targetWidth, targetHeight, this.grabbedAlloc.grabAngle, this.grabbedAlloc.flippedHorz, { x: sheetCoordX, y: sheetCoordY })
        if(insertResult) {
          moveAlloc = insertResult

          // Fill in order item info for rendering shape info
          if(this.grabbedAlloc.panelDownloadInfo) {
            moveAlloc.orderItem = {
              isEditorGeneratedItem: this.grabbedAlloc.panelDownloadInfo.isEditorGeneratedItem,
              shapeData: this.grabbedAlloc.panelDownloadInfo.shapeData,
            }
            moveAlloc.trimTop = this.grabbedAlloc.panelDownloadInfo.trimTop
            moveAlloc.trimRight = this.grabbedAlloc.panelDownloadInfo.trimRight
            moveAlloc.trimBottom = this.grabbedAlloc.panelDownloadInfo.trimBottom
            moveAlloc.trimLeft = this.grabbedAlloc.panelDownloadInfo.trimLeft
            moveAlloc.cutoutList = (this.grabbedAlloc.panelDownloadInfo.defaultCutoutList || []).map(cutoutInfo => {
              return {
                index: cutoutInfo.index
              }
            })
          }

          this.grabbedAlloc.allocId = moveAlloc.id
        }
      }
    }

    let canvas = this.canvasElement
    let ctx = canvas.getContext('2d')

    this.render(this.moveSheetCopy)

    // Draw the floating outline for the grabbed allocation
    ctx.save()
    ctx.translate(this.info.originOffsetX, this.info.originOffsetY);
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 2

    // Scratchpad
    if(scratchPadInsert) {
      ctx.fillStyle = this.settings.draggingScratchFillStyle
      ctx.fillRect(this.transformPointX(targetX), this.transformPointY(targetY), this.transformPointX(targetWidth), this.transformPointY(targetHeight))

      ctx.strokeStyle = this.settings.draggingScratchStrokeStyle    
      ctx.strokeRect(this.transformPointX(targetX), this.transformPointY(targetY), this.transformPointX(targetWidth), this.transformPointY(targetHeight))

    } else if(!insertResult) {
      // Fail result
      ctx.fillStyle = this.settings.draggingFailFillStyle
      ctx.fillRect(this.transformPointX(targetX), this.transformPointY(targetY), this.transformPointX(targetWidth), this.transformPointY(targetHeight))

      ctx.strokeStyle = this.settings.draggingFailStrokeStyle    
      ctx.strokeRect(this.transformPointX(targetX), this.transformPointY(targetY), this.transformPointX(targetWidth), this.transformPointY(targetHeight))
    }
    else if(insertResult) {    
      // Show success result
      ctx.fillStyle = this.settings.draggingSuccessFillStyle
      ctx.fillRect(this.transformPointX(moveAlloc.x), this.transformPointY(moveAlloc.y), this.transformPointX(moveAlloc.width), this.transformPointY(moveAlloc.height))

      ctx.strokeStyle = this.settings.draggingSuccessStrokeStyle
      ctx.strokeRect(this.transformPointX(moveAlloc.x), this.transformPointY(moveAlloc.y), this.transformPointX(moveAlloc.width), this.transformPointY(moveAlloc.height))

      ctx.strokeStyle = this.settings.draggingStrokeStyle
      ctx.strokeRect(this.transformPointX(targetX), this.transformPointY(targetY), this.transformPointX(targetWidth), this.transformPointY(targetHeight))
    }

    // Downloading Icons
    if(this.grabbedAlloc.isNewPanel) {
      let fontCloudDownload = '\uf0ed'
      let fontExclamationTriangle = '\uf071'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.font = `900 32px "Font Awesome 5 Pro"`;

      if(this.grabbedAlloc.panelDownloading) {
        ctx.fillStyle = '#000000'
        ctx.fillText(fontCloudDownload, this.transformPointX(moveAlloc.x + moveAlloc.width/2), this.transformPointY(moveAlloc.y + moveAlloc.height/2))
      } else if(this.grabbedAlloc.panelDownloadFailed) {
        ctx.fillStyle = '#FF0000'
        ctx.fillText(fontExclamationTriangle, this.transformPointX(moveAlloc.x + moveAlloc.width/2), this.transformPointY(moveAlloc.y + moveAlloc.height/2))

      }
    }

    ctx.restore()
  }


  /**
   * Render the editor to the canvas
   */
  render(sheet = null) {
    let canvas = this.canvasElement
    let ctx = canvas.getContext('2d')
    let ctr = this.canvasContainerElement
    let scale = this.getScale()

    // Ignore if scale is invalid. Can occur when hiding/showing the editor canvas
    if(scale <= 0)
      return

    canvas.width = ctr.clientWidth
    canvas.height = ctr.clientHeight

    ctx.fillStyle = this.settings.backgroundColour
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    if(!sheet)
      sheet = this.editingSheet

    if(!sheet)
      return

    ctx.save()
    ctx.translate(this.info.originOffsetX, this.info.originOffsetY);
    
    // Render the sheet
    ctx.fillStyle = this.settings.sheetBaseColour
    ctx.fillRect(0, 0, sheet.width * scale, sheet.height * scale)

    // Allocations
    sheet.optimiserSheetAllocations.forEach(alloc => {
      let isCompleted = alloc.status == SheetAllocationStatusTypes.completed

      if(alloc.type == SheetAllocationTypes.panel || alloc.type == SheetAllocationTypes.panelScratchPad) {

        ctx.fillStyle = isCompleted ? this.settings.completedPanelColour : this.settings.panelColour
        if(alloc.type == SheetAllocationTypes.panelScratchPad)
          ctx.fillStyle = this.settings.scratchPadPanelColour

        ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))

        // Render the panel trim overrides
        if(alloc.isPanelTrimOverridden) {
          this.renderProductionPanelTrim(ctx, alloc)
        }

        // Render the panel outline
        // Ignore if panel size is overridden or shape cutting is disabled
        if(alloc.orderItem && alloc.orderItem.isEditorGeneratedItem && !alloc.isPanelSizeOverridden && !alloc.isPanelShapeCuttingDisabled) {
          this.renderProductionPanelShape(ctx, alloc)

          this.renderProductionPanelCutouts(ctx, alloc)
        }

        // Render edgeworks & shaped cuts
        if(alloc.orderItem && !alloc.isPanelSizeOverridden && !alloc.isPanelShapeCuttingDisabled) {
          this.renderProductionPanelEdgework(ctx, alloc)

          this.renderProductionPanelCuts(ctx, alloc)
        }

        // Special Outline for panel size overridden allocations
        if(alloc.isPanelSizeOverridden) {
          ctx.save()
          ctx.lineWidth = 2
          ctx.strokeStyle = this.settings.overriddenPanelStrokeStyle
          ctx.setLineDash([4, 4]);
          ctx.strokeRect(this.transformPointX(alloc.x)+1, this.transformPointY(alloc.y)+1, this.transformPointX(alloc.width)-2, this.transformPointY(alloc.height)-2)
          ctx.restore()
        }

        // Relief cuts
        let reliefCuts = alloc.generateTransformedReliefCuts()
        reliefCuts.forEach(reliefCut => {
          ctx.save()
          ctx.lineWidth = 2
          ctx.strokeStyle = this.settings.reliefCutStrokeStyle
          ctx.beginPath()
          ctx.moveTo(this.transformPointX(reliefCut.startPosition.x), this.transformPointY(reliefCut.startPosition.y))
          ctx.lineTo(this.transformPointX(reliefCut.endPosition.x), this.transformPointY(reliefCut.endPosition.y))
          ctx.stroke()
          ctx.restore()
        })

        if(alloc.productionPanel && alloc.productionPanel.barcode && alloc.order) {
          let labelText = [alloc.productionPanel.barcode]

          if(alloc.isPanelSizeOverridden)
            labelText.push(`Override: ${alloc.sourceHeight}x${alloc.sourceWidth}`)
          else
            labelText.push(`${alloc.productionPanel.panelHeight}x${alloc.productionPanel.panelWidth}`)

          labelText.push(alloc.order.sequenceNumber)

          this.renderAllocLabels(ctx, alloc, labelText)
        }
      }
      if(alloc.type == SheetAllocationTypes.blankPanel) {

        ctx.fillStyle = this.settings.blankPanelColour
        ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))

        let labelText = ['BLANK']
        if(alloc.userInfo)
          labelText.push(alloc.userInfo)
        labelText.push(`${alloc.getBoundsHeight()}x${alloc.getBoundsWidth()}`)

        this.renderAllocLabels(ctx, alloc, labelText)
      }

      if(alloc.type == SheetAllocationTypes.offcut) {

        ctx.fillStyle = isCompleted ? this.settings.completedOffcutColour : this.settings.offcutColour
        ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))

        let labelText = ['OFFCUT']
        if(alloc.glassLevel && alloc.glassLevel.barcode)
          labelText.push(alloc.glassLevel.barcode)
        labelText.push(`${alloc.getBoundsHeight()}x${alloc.getBoundsWidth()}`)

        this.renderAllocLabels(ctx, alloc, labelText)
      }

      if(alloc.type == SheetAllocationTypes.defect) {

        ctx.fillStyle = this.settings.defectColour
        ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))

        let labelText = ['DEFECT']
        labelText.push(`${alloc.height}x${alloc.width}`)

        this.renderAllocLabels(ctx, alloc, labelText)
      }

      if(alloc.type == SheetAllocationTypes.splitPanel) {

        ctx.fillStyle = this.settings.splitPanelColour
        ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))

        let labelText = ['SPLIT PANEL']
        if(alloc.productionPanel && alloc.productionPanel.barcode) {
          labelText.push(alloc.productionPanel.barcode)
        }
        labelText.push(`${alloc.height}x${alloc.width}`)

        this.renderAllocLabels(ctx, alloc, labelText)
      }
    })


    // Display grain direction (if set)
    if(sheet.isStockUsingGrain) {
      this._renderSheetGrain(ctx, sheet)
    }

    // Allocation icons / dimensions
    sheet.optimiserSheetAllocations.forEach(alloc => {

      // Render allocation icons
      this._renderAllocationIcons(ctx, alloc)

      // Render allocation dimensions
      this._renderAllocationDimensions(ctx, alloc)
    })

    // Sheet Node borders
    this._renderSheetNodeBorder(ctx, sheet, sheet.rootNode)

    // Highlight / Selections
    let selectedAllocs = []
    let highlightedAlloc = null    
    sheet.optimiserSheetAllocations.forEach(alloc => {      
      if(alloc.highlighted && !alloc.selected)
        highlightedAlloc = alloc
      if(alloc.selected)
        selectedAllocs.push(alloc)
    })

    // Show highlighted allocation
    if(highlightedAlloc) {
      ctx.lineWidth = 2
      ctx.strokeStyle = this.settings.highlightStrokeStyle
      ctx.strokeRect(this.transformPointX(highlightedAlloc.getBoundsLeft()), this.transformPointY(highlightedAlloc.getBoundsTop()), this.transformPointX(highlightedAlloc.getBoundsWidth()), this.transformPointY(highlightedAlloc.getBoundsHeight()))
    }
    // Show selected allocations
    selectedAllocs.forEach(alloc => {
      ctx.lineWidth = 2
      ctx.strokeStyle = this.settings.selectedStrokeStyle
      ctx.fillStyle = this.settings.selectedFillStyle
      ctx.strokeRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))
      ctx.fillRect(this.transformPointX(alloc.getBoundsLeft()), this.transformPointY(alloc.getBoundsTop()), this.transformPointX(alloc.getBoundsWidth()), this.transformPointY(alloc.getBoundsHeight()))
    })

    // Highlight info
    switch(this.highlightInfo.type) {
      case OptimiserEditorSelectionTypes.nodeEdge:
        ctx.lineWidth = 4
        ctx.strokeStyle = this.settings.highlightEdgeStrokeStyle
        ctx.beginPath()
        ctx.moveTo(this.transformPointX(this.highlightInfo.startPosition.x), this.transformPointY(this.highlightInfo.startPosition.y))
        ctx.lineTo(this.transformPointX(this.highlightInfo.endPosition.x), this.transformPointY(this.highlightInfo.endPosition.y))
        ctx.stroke()
        break

      case OptimiserEditorSelectionTypes.cuttingLine:       
        this._renderEditorSegmentList(ctx, null, [this.highlightInfo.segment], {
          lineWidth: 5,
          strokeStyle: this.settings.highlightCuttingLineStrokeStyle,
          stroke: true
        })

        // Render the nearest point on the cutting line
        ctx.fillStyle = this.settings.highlightCuttingLineNearestPointFillStyle
        ctx.beginPath()
        ctx.arc(this.transformPointX(this.highlightInfo.sheetPoint.x), this.transformPointY(this.highlightInfo.sheetPoint.y), 5, 0, Math.PI*2)
        ctx.fill()
        break

      case OptimiserEditorSelectionTypes.reliefCut:
        this._renderEditorSegmentList(ctx, null, [this.highlightInfo.segment], {
          lineWidth: 5,
          strokeStyle: this.settings.highlightReliefCutStrokeStyle,
          stroke: true
        })
        break

      case OptimiserEditorSelectionTypes.cutout:
        this._renderEditorSegmentList(ctx, null, this.highlightInfo.path, {
          lineWidth: 5,
          strokeStyle: this.settings.highlightCutoutStrokeStyle,
          stroke: true
        })
        break
    }

    // Selection box
    if(this.selectionBox.active) {
      ctx.lineWidth = 1
      ctx.strokeStyle = this.settings.selectionBoxStrokeStyle
      ctx.fillStyle = this.settings.selectionBoxFillStyle

      let boxStartX = (this.selectionBox.startMouseX - this.info.originOffsetX) / scale
      let boxStartY = (this.selectionBox.startMouseY - this.info.originOffsetY) / scale
      let boxEndX = (this.selectionBox.currentMouseX - this.info.originOffsetX) / scale
      let boxEndY = (this.selectionBox.currentMouseY - this.info.originOffsetY) / scale

      let selX = Math.min(boxStartX, boxEndX)
      let selY = Math.min(boxStartY, boxEndY)
      let selWidth = Math.abs(boxEndX - boxStartX)
      let selHeight = Math.abs(boxEndY - boxStartY)

      ctx.strokeRect(this.transformPointX(selX), this.transformPointY(selY), this.transformPointX(selWidth), this.transformPointY(selHeight))
      ctx.fillRect(this.transformPointX(selX), this.transformPointY(selY), this.transformPointX(selWidth), this.transformPointY(selHeight))
    }

    ctx.restore()
  }


  /**
   * Render the sheet's grain direction display
   */
  _renderSheetGrain(ctx, sheet) {
    let scale = this.getScale()
    let arrowWidth = 1 / scale
    let arrowHeight = 2 / scale
    let spacing = 10 / scale

    // Ignore if no grain direction set
    if(sheet.grainDirection == StockGrainDirectionTypes.none)
      return

    // Note: GrainDirection is rotated by server to match isRotated
    let grainDirection = sheet.grainDirection

    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)'
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'

    // Arrow heads
    ctx.beginPath()
    if(grainDirection == StockGrainDirectionTypes.vertical) {
      for(let i=spacing; i<sheet.width * scale; i+=spacing) {
        ctx.moveTo(i, 0)
        ctx.lineTo(i + arrowWidth, arrowHeight)
        ctx.lineTo(i - arrowWidth, arrowHeight)
        ctx.moveTo(i, sheet.height * scale)
        ctx.lineTo(i + arrowWidth, sheet.height * scale - arrowHeight)
        ctx.lineTo(i - arrowWidth, sheet.height * scale - arrowHeight)
      }
    } else {
      for(let i=spacing; i<sheet.height * scale; i+=spacing) {
        ctx.moveTo(0, i)
        ctx.lineTo(arrowHeight, i - arrowWidth)
        ctx.lineTo(arrowHeight, i + arrowWidth)
        ctx.moveTo(sheet.width * scale, i)
        ctx.lineTo(sheet.width * scale - arrowHeight, i - arrowWidth, )
        ctx.lineTo(sheet.width * scale - arrowHeight, i + arrowWidth)
      }

    }
    ctx.fill()

    // Lines
    ctx.beginPath()
    if(grainDirection == StockGrainDirectionTypes.vertical) {
      for(let i=spacing; i<sheet.width * scale; i+=spacing) {
        ctx.moveTo(i, arrowHeight)
        ctx.lineTo(i, sheet.height * scale - arrowHeight)
      }
    } else {
      for(let i=spacing; i<sheet.height * scale; i+=spacing) {
        ctx.moveTo(arrowHeight, i)
        ctx.lineTo(sheet.width * scale - arrowHeight, i)
      }
    }
    ctx.stroke()
  }


  /**
   * Render a sheet node's border in the cutting tree
   */
  _renderSheetNodeBorder(ctx, sheet, node, idList = []) {
    if(!node)
      return

    var nodeLeft = node.x
    var nodeTop = node.y
    var nodeWidth = node.width
    var nodeHeight = node.height

    // If an offcut, account for offcut overflows
    if(node.type == OptimiserSheetNodeTypes.offcut) {
      var alloc = sheet.findAllocationAtNodeId(node.id)
      if(alloc) {
        nodeLeft = alloc.getBoundsLeft()
        nodeTop = alloc.getBoundsTop()
        nodeWidth = alloc.getBoundsWidth()
        nodeHeight = alloc.getBoundsHeight()
      }
    }

    if(node.type != OptimiserSheetNodeTypes.node) {
      ctx.strokeStyle = this.settings.borderStrokeStyle
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(this.transformPointX(nodeLeft), this.transformPointY(nodeTop))
      ctx.lineTo(this.transformPointX(nodeLeft + nodeWidth), this.transformPointY(nodeTop))

      ctx.moveTo(this.transformPointX(nodeLeft), this.transformPointY(nodeTop + nodeHeight))
      ctx.lineTo(this.transformPointX(nodeLeft + nodeWidth), this.transformPointY(nodeTop + nodeHeight))

      ctx.moveTo(this.transformPointX(nodeLeft + nodeWidth), this.transformPointY(nodeTop))
      ctx.lineTo(this.transformPointX(nodeLeft + nodeWidth), this.transformPointY(nodeTop + nodeHeight))

      ctx.moveTo(this.transformPointX(nodeLeft), this.transformPointY(nodeTop))
      ctx.lineTo(this.transformPointX(nodeLeft), this.transformPointY(nodeTop + nodeHeight))
      ctx.stroke()
    }

    let child1List = _.clone(idList)
    let child2List = _.clone(idList)

    // Debugging: Display the node ID
    if(this.developerDisplayEnabled) {
      child1List.push(node.id)
      child2List.push(node.id)

      ctx.fillStyle = '#000000'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'
      ctx.font = `12px sans-serif`
      ctx.fillText(`${child1List.join(' - ')}`, this.transformPointX(nodeLeft + 50), this.transformPointY(nodeTop) + 10)
    }

    this._renderSheetNodeBorder(ctx, sheet, node.childNodes[0], child1List)
    this._renderSheetNodeBorder(ctx, sheet, node.childNodes[1], child2List)
  }


  /**
   * Render a list of labels for an allocated rectangle
   * Scales text to stay within the rectangle
   */
  renderAllocLabels(ctx, alloc, textList) {
    let minSize = 200
    if(alloc.getBoundsWidth() < minSize && alloc.getBoundsHeight() < minSize)
      return

    ctx.fillStyle = this.settings.textFillStyle
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Scale font to match alloc size
    let fontWidthScaleFactor = 15.0
    let fontHeightScaleFactor = 8.0
    let scaledWidthfontSize = Math.round((alloc.getBoundsWidth() * this.getScale()) / fontWidthScaleFactor)
    let scaledHeightfontSize = scaledWidthfontSize
    if(alloc.getBoundsHeight() < 200)
      scaledHeightfontSize = Math.round((alloc.getBoundsHeight() * this.getScale()) / fontHeightScaleFactor)

    let fontSize = Math.min(scaledWidthfontSize, scaledHeightfontSize)
    if(fontSize < 4)
      return

    fontSize = Math.min(fontSize, 14)
    fontSize = Math.max(fontSize, 6)

    let lineHeight = fontSize * 1.25
    ctx.font = `${fontSize}px sans-serif`

    let currentY = this.transformPointY(alloc.getBoundsTop() + alloc.getBoundsHeight()/2)
    currentY -= Math.round((textList.length * lineHeight) / 2)

    textList.forEach((text, index) => {
      ctx.fillText(text, this.transformPointX(alloc.getBoundsLeft() + alloc.getBoundsWidth()/2), Math.round(currentY))
      currentY += lineHeight
    })
  }


  /**
   * Render a production panel's shape
   * Used for panels generated by the order editor for non-rectangle outlines and/or center cutouts
   */
  renderProductionPanelShape(ctx, alloc) {
    // Display the outline path
    let outlinePath = alloc.generateTransformedPath()
    this._renderEditorSegmentList(ctx, alloc, outlinePath, {
      strokeStyle: this.settings.panelShapeStrokeStyle,
      fillStyle: this.settings.panelShapeFillStyle,
      stroke: true,
      fill: true
    })

    // Display the square corners
    this._renderPanelSquareCorners(ctx, outlinePath)

    // Holes
    let holes = alloc.generateTransformedHoles()
    holes.forEach(holeInfo => {
      this._renderEditorSegmentList(ctx, alloc, holeInfo.path, {
        strokeStyle: this.settings.panelShapeStrokeStyle,
        fillStyle: this.settings.panelHoleFillStyle,
        stroke: true,
        fill: true
      })
    })
  }


  /**
   * Render a production panel's cutouts that are flagged for cutting at optimisation
   */
  renderProductionPanelCutouts(ctx, alloc) {
    let flaggedCutoutList = alloc.cutoutList
    let transformedCutoutList = alloc.generateTransformCutouts()

    flaggedCutoutList.forEach(cutoutInfo => {
      if(cutoutInfo.index >= 0 && cutoutInfo.index < transformedCutoutList.length) {

        var cutoutData = transformedCutoutList[cutoutInfo.index]
        this._renderEditorSegmentList(ctx, alloc, cutoutData.path, {
          strokeStyle: this.settings.cutoutCutStokeStyle,
          stroke: true,
          lineWidth: 3,
        })
      }
    });

  }


  /**
   * Render a production panel's trim overrides in an allocation
   * Used when an allocation has extra trim added
   */
  renderProductionPanelTrim(ctx, alloc) {
    ctx.fillStyle = this.settings.panelTrimFillStyle

    var trimInfo = alloc.generateTransformTrimValues()
   
    if(trimInfo.trimTop > 0)
      ctx.fillRect(this.transformPointX(alloc.x), this.transformPointY(alloc.y), this.transformPointX(alloc.width), this.transformPointY(trimInfo.trimTop))
    if(trimInfo.trimRight > 0)
      ctx.fillRect(this.transformPointX(alloc.x + alloc.width - trimInfo.trimRight), this.transformPointY(alloc.y), this.transformPointX(trimInfo.trimRight), this.transformPointY(alloc.height))
    if(trimInfo.trimBottom > 0)
      ctx.fillRect(this.transformPointX(alloc.x), this.transformPointY(alloc.y + alloc.height - trimInfo.trimBottom), this.transformPointX(alloc.width), this.transformPointY(trimInfo.trimBottom))
    if(trimInfo.trimLeft > 0)
      ctx.fillRect(this.transformPointX(alloc.x), this.transformPointY(alloc.y), this.transformPointX(trimInfo.trimLeft), this.transformPointY(alloc.height))
  }


  /**
   * Render the production panels edgework
   * Used for both order editor generated panels, and line-item based simple rectangle panels
   */
  renderProductionPanelEdgework(ctx, alloc) {
    let edgeworkThickness = 3

    let edgeworkList = alloc.generateTransformEdgeworks()

    edgeworkList.forEach(edgeworkInfo => {
      this._renderEditorSegmentList(ctx, alloc, edgeworkInfo.segments, {
        strokeStyle: edgeworkInfo.colour,
        stroke: true,
        lineWidth: edgeworkThickness
      })
    })
  }


  /**
   * Render a production panel's cuts
   * Used for panels generated by the order editor for non-rectangle outlines and/or center cutouts
   */
  renderProductionPanelCuts(ctx, alloc) {
    // Display the cuts that will be part of the optimiser export
    let cuttingList = alloc.generateTransformedCuttingList()
    cuttingList.forEach(cuttingSegment => {
      this._renderEditorSegmentList(ctx, alloc, [cuttingSegment], {
        strokeStyle: this.settings.shapedCutStrokeStyle,
        stroke: true,
        lineWidth: 2,
        lineDash: [3, 3]
      })
    })
  }


  /**
   * Render a production panel's square corners in the shape segment list
   */
  _renderPanelSquareCorners(ctx, transformedShapePath) {
    var minSegmentLength = 75
    var maxCornerRadius = 10
    var angleDisplayPrecision = 2
    var squareSize = 10 / this.getScale()

    ctx.save()
    ctx.strokeStyle = this.settings.panelSquareCornerStrokeStyle
    ctx.lineWidth = 1
    //ctx.resetTransform()

    transformedShapePath.forEach((segment, segmentIndex) => {
      var nextSegment = transformedShapePath[segmentIndex == transformedShapePath.length-1 ? 0 : segmentIndex+1]

      // Only display angles between 2 straight lines
      if(segment.type == SegmentTypes.line && nextSegment.type == SegmentTypes.line) {
        var primaryDirection = segment.getDirection()
        var angleDirection = nextSegment.getDirection()

        var primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(primaryDirection)))
        var angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(angleDirection)))

        var angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(primaryDirDegrees, angleDirDegrees), angleDisplayPrecision)

        // Don't show anything if 0 or 180 degrees
        if(MathsUtils.roundNumber(Math.abs(angle - 180.0), angleDisplayPrecision) == 0 || MathsUtils.roundNumber(Math.abs(angle), angleDisplayPrecision) == 0)
          return

        // Ignore if either segment is too small
        if(segment.getLength() < minSegmentLength || nextSegment.getLength() < minSegmentLength)
          return

        // Ignore if a corner radius exists
        if(segment.endCornerRadius >= maxCornerRadius)
          return

        var isSquareAngle = MathsUtils.roundNumber(Math.abs(angle - 90), angleDisplayPrecision) == 0
        if(isSquareAngle) {
          // Square angle marker
          var sqDisplayPoint = segment.endPosition
          var sqStartPnt = new THREE.Vector3(sqDisplayPoint.x - primaryDirection.x * squareSize, sqDisplayPoint.y - primaryDirection.y * squareSize)
          var sqMidPoint = sqStartPnt.clone().addScaledVector(angleDirection, squareSize)
          var sqEndPoint = new THREE.Vector3(sqDisplayPoint.x + angleDirection.x * squareSize, sqDisplayPoint.y + angleDirection.y * squareSize)

          ctx.beginPath()
          ctx.moveTo(this.transformPointX(sqStartPnt.x), this.transformPointY(sqStartPnt.y))
          ctx.lineTo(this.transformPointX(sqMidPoint.x), this.transformPointY(sqMidPoint.y))
          ctx.lineTo(this.transformPointX(sqEndPoint.x), this.transformPointY(sqEndPoint.y))
          ctx.stroke()
        }
      }
    })

    ctx.restore()
  }


  /**
   * Render an editor stored segment list associated with a productional panel's OrderItem:shapeData field
   * Used to render an outline for a production panel shape / center cutout
   */
  _renderEditorSegmentList(ctx, alloc, segmentInfoList, options = {}) {
    ctx.save()
    ctx.strokeStyle = options.strokeStyle || ''
    ctx.fillStyle = options.fillStyle || ''
    ctx.setLineDash(options.lineDash || []);
    ctx.lineWidth = options.lineWidth || 1
    //ctx.resetTransform()

    ctx.beginPath()

    segmentInfoList.forEach((segment, segmentIndex) => {
      let prevSegment = segmentInfoList[segmentIndex == 0 ? segmentInfoList.length-1 : segmentIndex-1]
      let nextSegment = segmentInfoList[segmentIndex == segmentInfoList.length-1 ? 0 : segmentIndex+1]

      if(segmentIndex == 0) {
        if(segment.type == SegmentTypes.line) {
          if(segment.startCornerRadius == 0.0) {
            ctx.moveTo(this.transformPointX(segment.startPosition.x), this.transformPointY(segment.startPosition.y))
          } else {
            // Adjust for the corner radius
            let roundCornerData = MathsUtils.calculateCornerRounding(
              prevSegment.startPosition,
              prevSegment.endPosition,
              segment.startPosition,
              segment.endPosition,
              segment.startCornerRadius,
              false
            )

            if(!roundCornerData) {
              ctx.moveTo(this.transformPointX(segment.startPosition.x), this.transformPointY(segment.startPosition.y))
            } else {
              ctx.moveTo(this.transformPointX(roundCornerData.segment2Start.x), this.transformPointY(roundCornerData.segment2Start.y))
            }
          }
        }
      }

      if(segment.type == SegmentTypes.line) {
        if(segment.endCornerRadius == 0.0) {
          ctx.lineTo(this.transformPointX(segment.endPosition.x), this.transformPointY(segment.endPosition.y))
        } else {
          // Adjust for the corner radius
          let roundCornerData = MathsUtils.calculateCornerRounding(
            segment.startPosition,
            segment.endPosition,
            nextSegment.startPosition,
            nextSegment.endPosition,
            segment.endCornerRadius,
            false
          )

          if(!roundCornerData) {
            ctx.lineTo(this.transformPointX(segment.endPosition.x), this.transformPointY(segment.endPosition.y))
          } else {
            ctx.lineTo(this.transformPointX(roundCornerData.segment1End.x), this.transformPointY(roundCornerData.segment1End.y))

            ctx.arc(
              this.transformPointX(roundCornerData.center.x),
              this.transformPointY(roundCornerData.center.y),
              roundCornerData.radius * this.getScale(),
              roundCornerData.startAngle,
              roundCornerData.endAngle,
              roundCornerData.clockwise
            )
          }
        }

      } else if(segment.type == SegmentTypes.arc) {
        if(segmentIndex == 0)
          ctx.moveTo(this.transformPointX(segment.startPosition.x), this.transformPointY(segment.startPosition.y))

        ctx.arc(
          this.transformPointX(segment.arcCenterPosition.x),
          this.transformPointY(segment.arcCenterPosition.y),
          segment.arcRadius * this.getScale(),
          MathsUtils.degreesToRadians(segment.arcStartAngle),
          MathsUtils.degreesToRadians(segment.arcEndAngle),
          !segment.arcClockwise
        )
      }
    })

    if(options.fill)
      ctx.fill()
    if(options.stroke)
      ctx.stroke()
    ctx.restore()
  }


  /**
   * Render a straight line cut for an allocation
   */
  _renderEditorStraightLineCut(ctx, alloc, startPosition, endPosition) {
    ctx.save()
    ctx.strokeStyle = this.settings.straightLineCutStrokeStyle
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3]);
    //ctx.resetTransform()

    ctx.beginPath()
    ctx.moveTo(this.transformPointX(startPosition.x), this.transformPointY(startPosition.y))
    ctx.lineTo(this.transformPointX(endPosition.x), this.transformPointY(endPosition.y))

    ctx.stroke()
    ctx.restore()
  }


  /**
   * Reset zoom to fit the sheet contents
   */
  zoomToFitSheet() {
    this._updateSheetScale()
    this.render()
  }


  /**
   * Zoom to an allocation
   */
  zoomToAllocation(alloc) {
    let ctr = this.canvasContainerElement
    let padding = 15

    let destWidth = ctr.clientWidth - padding
    let destHeight = ctr.clientHeight - padding

    this.info.scale = Math.min(destWidth / alloc.width, destHeight / alloc.height);
    this.info.zoomStep = this.calculateZoomStepFromScale(this.info.scale);
    this.info.originOffsetX = -(alloc.x + alloc.width/2) * this.info.scale + ctr.clientWidth/2;
    this.info.originOffsetY = -(alloc.y + alloc.height/2) * this.info.scale + ctr.clientHeight/2;

    this.clampCameraPosition()

    this.render()
  }


  /**
   * Calculate & update the the scale & zoom info to display the sheet within the area
   */
  _updateSheetScale() {
    let ctr = this.canvasContainerElement
    let padding = 15

    let destWidth = ctr.clientWidth - padding - this.scratchPadSize
    let destHeight = ctr.clientHeight - padding

    this.info.scale = Math.min(destWidth / this.editingSheet.width, destHeight / this.editingSheet.height);
    this.info.zoomStep = this.calculateZoomStepFromScale(this.info.scale);
    this.info.originOffsetX = 0;
    this.info.originOffsetY = 0;
  }


  /**
   * Get the scale for the editor
   */
  getScale() {
    return this.info.scale
  }


  /**
   * Get the origin offset for the editor
   */
  getOriginOffsetX() {
    return this.info.originOffsetX
  }
  getOriginOffsetY() {
    return this.info.originOffsetY
  }


  /**
   * Window was resized event
   */
  onResize() {
    if(!this.editingSheet)
      return
    this._updateSheetScale()
  }


  /**
   * Get if the editor is sending a layout update
   */
  isSendingLayoutUpdate() {
    return this.info.sendingLayoutUpdate
  }


  /**
   * Transform a position with pixel adjust to prevent blurry lines
   */
  transformPointX(x) {
    return Math.round(x * this.getScale()) + 0.5
  }
  transformPointY(y) {
    return Math.round(y * this.getScale()) + 0.5
  }


  /**
   * Render icons for the allocation item
   * Used to flag items, such as on hold, completed, cancelled, etc
   * Uses FontAwesome to render icon glyphs
   * 
   */
   _renderAllocationIcons(ctx, alloc) {
    let iconPadding = 20
    let iconWidth = 19
    let iconFontSize = 16
    let left = this.transformPointX(alloc.getBoundsLeft() + iconPadding)
    let top = this.transformPointX(alloc.getBoundsTop() + iconPadding)

    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.font=`900 ${iconFontSize}px "Font Awesome 5 Pro"`;

    // For unicode IDs, see all.css in FontAwesome package
    let fontCheckCircle = '\uF058'
    let fontPauseCircle = '\uF28b'
    let fontExclamationCircle = '\uF06a'
    let fontLock = '\uF023'
    let fontPencilRuler = '\uf5ae'
    let fontScanner = '\uf8f3'
    let fontTimesOctagon = '\uf2f0'
    let fontScalpelPath = '\uf61e'

    let iconList = []

    let isPanel = alloc.type == SheetAllocationTypes.panel
    let isPanelScratchPad = alloc.type == SheetAllocationTypes.panelScratchPad
    let isBlankPanel = alloc.type == SheetAllocationTypes.blankPanel
    let isSplitPanel = alloc.type == SheetAllocationTypes.splitPanel
    let isCompleted = alloc.status == SheetAllocationStatusTypes.completed

    if(alloc.type == SheetAllocationTypes.panel && (!alloc.productionPanel || !alloc.productionPanel.id))
      return

    // Cutting Index
    if(this.editingSheet.status == OptimiserSheetStatusTypes.cutting || this.editingSheet.status == OptimiserSheetStatusTypes.completed) {
      let displayCuttingIndex = null
      if(alloc.type == SheetAllocationTypes.panel && alloc.productionPanel.cuttingIndex)
        displayCuttingIndex = alloc.productionPanel.cuttingIndex
      if(alloc.type == SheetAllocationTypes.offcut && alloc.glassLevel.cuttingIndex)
        displayCuttingIndex = alloc.glassLevel.cuttingIndex

      if(displayCuttingIndex ) {
        let cuttingIconFontSize = 26
        let cuttingBadgeSize = cuttingIconFontSize*1.4

        ctx.fillStyle = 'rgba(0,0,0,0.5)'
        if(isCompleted)
          ctx.fillStyle = 'rgba(0,196,0,0.5)'
        ctx.beginPath()
        ctx.arc(left + cuttingBadgeSize/2, top + cuttingBadgeSize/2, cuttingBadgeSize/2, 0, 2*Math.PI)
        ctx.fill()

        ctx.textAlign = 'center'
        ctx.fillStyle = '#ffffff'
        ctx.font=`${cuttingIconFontSize}px Arial`;
        ctx.fillText(`${displayCuttingIndex}`, left + cuttingBadgeSize/2, top + 6);

        left += cuttingBadgeSize*1.1
      }
    }
    
    // Completed icon
    if(isCompleted) {
      iconList.push({ bgColour: '#ffffff', colour: '#4eb93c', icon: fontCheckCircle })
    }

    // Lock icon
    // All panels are locked, so only display for other allocs
    if(!isPanel && !isPanelScratchPad && !isBlankPanel && alloc.isUserLocked) {
      iconList.push({ bgColour: null, colour: '#2366A8', icon: fontLock })
    }

    if((isPanel || isPanelScratchPad || isSplitPanel) && alloc.order && alloc.orderItem && alloc.productionPanel) {
      // Order on hold
      if(alloc.order.isOnHold) {
        iconList.push({ bgColour: '#ffffff', colour: '#ed6666', icon: fontPauseCircle })
      }

      // Order not in production/dispatch/pendingCOD
      // Note: Order can be in dispatch/pendingCOD when panel completed & scanned out
      if(alloc.order.status != OrderStatusTypes.production && alloc.order.status != OrderStatusTypes.despatch && alloc.order.status != OrderStatusTypes.pendingCODPaid) {
        iconList.push({ bgColour: '#000000', colour: '#fbc531', icon: fontExclamationCircle })
      }

      // NCR / Recut / Urgent
      if(alloc.productionPanel.isRecut || alloc.order.type == OrderTypes.nonChargeableRework || alloc.order.isUrgent) {
        iconList.push({ bgColour: '#ffffff', colour: '#ff0000', icon: fontExclamationCircle })
      }

      // Template handling requirement
      if(alloc.orderItem.isTemplateHandlingRequired) {
        iconList.push({ colour: alloc.isPanelTemplateHandled ? '#4eb93c' : '#ef6c00', icon: fontScanner, fontSize: 14 })
      }

      // Cutouts Require Handling
      if(alloc.isPanelCutoutsRequireHandling) {
        iconList.push({ colour: alloc.isPanelCutoutsHandled ? '#4eb93c' : '#ef6c00', icon: fontScalpelPath, fontSize: 14 })
      }

      // Custom Settings Overridden
      if(alloc.isPanelSizeOverridden || alloc.isPanelShapeCuttingDisabled) {
        iconList.push({ bgColour: '#ffffff', colour: '#079992', icon: fontPencilRuler })
      }

      // Cancelled
      if(alloc.productionPanel.status == ProductionPanelStatusTypes.cancelled || alloc.orderItem.isCancelled) {
        iconList.push({ bgColour: '#ffffff', colour: '#ed6666', icon: fontTimesOctagon })
      }
    }

    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    iconList.forEach(iconInfo => {
      var fontSize = iconInfo.fontSize || iconFontSize
      var bgCircleRadius = iconInfo.bgCircleRadius || (fontSize/2)

      ctx.font = `900 ${fontSize}px "Font Awesome 5 Pro"`;

      if(iconInfo.bgColour) {
        ctx.fillStyle = iconInfo.bgColour
        ctx.beginPath()
        ctx.arc(left + bgCircleRadius, top + bgCircleRadius, bgCircleRadius - 4, 0, 2*Math.PI)
        ctx.fill()
      }

      ctx.fillStyle = iconInfo.colour
      ctx.fillText(iconInfo.icon, left + bgCircleRadius, top + bgCircleRadius);
      left += iconWidth
    })
  }


  /**
   * Render the dimensions for an allocation
   * Scales or hides dimensions for rectangles that are too small
   */
  _renderAllocationDimensions(ctx, alloc) {
    ctx.save()
    let minSize = 70 / this.getScale()
    let spacer = 2

    let fontScaleFactor = 0.025
    let fontSize = Math.round(this.getScale() / fontScaleFactor)
    fontSize = Math.min(fontSize, 14)
    fontSize = Math.max(fontSize, 6)

    ctx.fillStyle = this.settings.textFillStyle
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.font = `${fontSize}px sans-serif`
    
    // Top
    if(alloc.getBoundsWidth() >= minSize) {
      ctx.fillText(Math.round(alloc.getBoundsWidth()), this.transformPointX(alloc.getBoundsLeft() + alloc.getBoundsWidth()/2), this.transformPointY(alloc.getBoundsTop()) + spacer)
    }

    // Left
    if(alloc.getBoundsHeight() >= minSize) {
      ctx.rotate(-Math.PI/2)
      ctx.fillText(Math.round(alloc.getBoundsHeight()), this.transformPointY(-(alloc.getBoundsTop() + alloc.getBoundsHeight()/2)), this.transformPointX(alloc.getBoundsLeft()) + spacer)
    }

    ctx.restore()
  }


  /**
   * Check what object is under the mouse position
   * Used for highlighting / selection
   * 
   * Returns an object containing details about the object (including type)
   * Sets type to 'none' if no object is under at the position
   */
  getSelectionInfoAtPosition(x, y) {
    let maxEdgeDistance = 12 / this.getScale()
    let maxCuttingLineDistance = 12 / this.getScale()

    let testPoint = new THREE.Vector2(x, y)
    let editingAvailable = this.isSheetEditingAvailable()

    // Edge check
    if(editingAvailable) {
      let node = this.editingSheet.findNodeAtPosition(x, y)
      if(node) {
        
        let edgeType = this.editingSheet.findNearestNodeEdgeAtPosition(node, x, y, maxEdgeDistance)
        if(edgeType) {
          let startPosition = null
          let endPosition = null

          let isEdgeSelectable = this.editingSheet.isEdgeExtendable(node, edgeType)
          if(isEdgeSelectable) {
            switch(edgeType) {
              case EdgeTypes.top:
                startPosition = new THREE.Vector2(node.getLeft(), node.getTop())
                endPosition = new THREE.Vector2(node.getRight(), node.getTop())
                break
              case EdgeTypes.right:
                startPosition = new THREE.Vector2(node.getRight(), node.getTop())
                endPosition = new THREE.Vector2(node.getRight(), node.getBottom())
                break
              case EdgeTypes.bottom:
                startPosition = new THREE.Vector2(node.getRight(), node.getBottom())
                endPosition = new THREE.Vector2(node.getLeft(), node.getBottom())
                break
              case EdgeTypes.left:
                startPosition = new THREE.Vector2(node.getLeft(), node.getBottom())
                endPosition = new THREE.Vector2(node.getLeft(), node.getTop())
                break
            }

            return {
              type: OptimiserEditorSelectionTypes.nodeEdge,
              startPosition: startPosition,
              endPosition: endPosition,
              edgeType: edgeType,
              nodeId: node.id,
              nodeRect: node.getRectangle()
            }
          }
        }
      }
    }

    // Allocation
    let sheetAlloc = this.editingSheet.getSheetAllocationAtPosition(x, y)
    if(sheetAlloc) {

      if(editingAvailable) {

        // Relief Cuts
        let transformedReliefCuts = sheetAlloc.generateTransformedReliefCuts()
        for(const [index, reliefCut] of transformedReliefCuts.entries()) {
          let point = reliefCut.projectPointOnSegment(testPoint, true)
          if(point.distanceTo(testPoint) < maxCuttingLineDistance) {
            let segmentRect = new Rectangle()

            // Move segment into sheet space for rendering
            let sheetReliefCut = reliefCut.clone()
            segmentRect.setFromBox2(sheetReliefCut.calculateBounds())

            return {
              type: OptimiserEditorSelectionTypes.reliefCut,
              point: point.clone().sub(new THREE.Vector2(sheetAlloc.x, sheetAlloc.y)),    // Relative to the allocation top-left
              sheetPoint: point,
              segment: sheetReliefCut,
              reliefCutIndex: index,
              rect: segmentRect,
              allocationId: sheetAlloc.id
            }
          }        
        }

        if(!sheetAlloc.isPanelSizeOverridden && !sheetAlloc.isPanelShapeCuttingDisabled) {

          // Cutting Lines
          let cuttingList = sheetAlloc.generateTransformedCuttingList()
          for(let cuttingSegment of cuttingList) {
            let point = cuttingSegment.projectPointOnSegment(testPoint, true)
            if(point.distanceTo(testPoint) < maxCuttingLineDistance) {
              let segmentRect = new Rectangle()
              segmentRect.setFromBox2(cuttingSegment.calculateBounds())

              return {
                type: OptimiserEditorSelectionTypes.cuttingLine,
                point: point.clone().sub(new THREE.Vector2(sheetAlloc.x, sheetAlloc.y)),    // Relative to the allocation top-left
                sheetPoint: point,
                segment: cuttingSegment.clone(),
                rect: segmentRect,
                allocationId: sheetAlloc.id,
                hasEdgework: cuttingSegment.hasEdgework,
                isCuttingSegment: cuttingSegment.isCuttingSegment,
                isBoundarySegment: cuttingSegment.isBoundarySegment,
                orderEditorEdgeworkConfigId: cuttingSegment.orderEditorEdgeworkConfigId,
                edgeworkColour: cuttingSegment.edgeworkColour,
                edgeworkAllowance: cuttingSegment.edgeworkAllowance,
                edgeworkValue: cuttingSegment.edgeworkValue
              }
            }
          }

          // Cutouts
          // Done by CNC / Waterjet, but can be converted to cut at optimiser station for certain cuts (eg, v-cuts, donuts)
          let cutoutList = sheetAlloc.generateTransformCutouts()
          for(const [index, cutout] of cutoutList.entries()) {

            if(cutout.bounds.containsPoint(testPoint)) {
              let cutoutRect = new Rectangle()
              cutoutRect.setFromBox2(cutout.bounds)

              return {
                type: OptimiserEditorSelectionTypes.cutout,
                point: testPoint.clone().sub(new THREE.Vector2(sheetAlloc.x, sheetAlloc.y)),    // Relative to the allocation top-left
                sheetPoint: testPoint,
                cutoutIndex: index,
                path: cutout.path,
                rect: cutoutRect,
                allocationId: sheetAlloc.id
              }
            }
          }
        }
      }

      return {
        type: OptimiserEditorSelectionTypes.allocation,
        allocationId: sheetAlloc.id
      }
    }


    return { type: OptimiserEditorSelectionTypes.none }
  }


  /**
   * Set the allocations in the sheet to be selected
   */
  setSelectedAllocations(allocs) {
    this.clearSelectedAllocation()

    allocs.forEach(alloc => {
      let sheetAlloc = this.editingSheet.optimiserSheetAllocations.find(sheetAlloc => sheetAlloc.id == alloc.id)
      if(sheetAlloc)
        sheetAlloc.selected = true
    })

    this.render()
  }


  /**
   * Clear the selected allocations
   */
  clearSelectedAllocation() {
    this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
      alloc.selected = false
    })
    this.render()
  }


  /**
   * Get the selected list of allocations
   */
  getSelectedAllocations() {
    return this.editingSheet.optimiserSheetAllocations.filter(alloc => alloc.selected)
  }


  /**
   * Get the bounds for all the selected allocations
   */
  getSelectedAllocationBounds() {
    let rect = new Rectangle()
    this.getSelectedAllocations().forEach(alloc => {
      rect.expandByRectangle(alloc.getRectangle())
    })

    return rect
  }


  /**
   * Clear the highlight info
   */
  clearHighlightInfo() {
    this.highlightInfo = {
      type: OptimiserEditorSelectionTypes.none
    }
  }


  /**
   * Clear the selection info
   */
  clearSelectedInfo() {
    this.selectedInfo = {
      type: OptimiserEditorSelectionTypes.none
    }
  }


  /**
   * Check if the edge highlight is selected
   */
  isEdgeSelected() {
    return this.selectedInfo.type == OptimiserEditorSelectionTypes.nodeEdge
  }


  /**
   * Check if the cutting line highlight is selected
   */
  isCuttingLineSelected() {
    return this.selectedInfo.type == OptimiserEditorSelectionTypes.cuttingLine
  }


  /**
   * Check if the relief Cut highlight is selected
   */
  isReliefCutSelected() {
    return this.selectedInfo.type == OptimiserEditorSelectionTypes.reliefCut
  }


  /**
   * Check if a cutout is selected
   */
  isCutoutSelected() {
    return this.selectedInfo.type == OptimiserEditorSelectionTypes.cutout
  }


  /**
   * Get the edge highlight info
   */
  getSelectedInfo() {
    return this.selectedInfo
  }


  /**
   * Check if the sheet has scratch pad panels existing
   */
  hasScratchPadPanelAllocations() {
    return this.editingSheet.optimiserSheetAllocations.filter(alloc => alloc.type == SheetAllocationTypes.panelScratchPad).length > 0
  }


  /**
   * Rotate the sheet
   * Rotates all allocations & packing data & sends the sheet update to the server
   */
  rotateSheet() {
    this.beginSheetUndoSnapshot()

    this.editingSheet.rotateSheet()

    this.commitUndoSnapshot()

    this.sendSheetLayoutUpdate({ resetViewport: true })
  }


  /**
   * Add a defect to the sheet
   * Done by editor user. Will add to the list & be sent to the server for final layout
   */
  addSheetDefect(defectInfo) {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    var defectId = this.editingSheet.getNextAllocationId()

    this.editingSheet.optimiserSheetAllocations.push(new OptimiserAllocation({
      type: SheetAllocationTypes.defect,
      id: defectId,
      x: defectInfo.x,
      y: defectInfo.y,
      width: defectInfo.width,
      height: defectInfo.height,
      sourceWidth: defectInfo.width,
      sourceHeight: defectInfo.height
    }))
  }


  /**
   * Add a blank area to the sheet
   * Done by editor user. Will add to the list & be sent to the server for final layout
   */
  addSheetBlank(blankInfo) {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    // Add blank to pending list
    this.addBlankPanelToPendingList({
      blankPanelUUID: blankInfo.blankPanelUUID,
      userInfo: blankInfo.userInfo,
      width: blankInfo.width,
      height: blankInfo.height,
    })
  }


  /**
   * Split an offcut allocation in half
   * Done by editor user
   */
  splitOffcutAllocation(id, splitDirectionType, splitDistance) {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.optimiserSheetAllocations.find(alloc => alloc.id == id)
    if(alloc) {
      if(alloc.type != SheetAllocationTypes.offcut) {
        this.editorComponent.errorMessage('Edit error', 'Only offcuts can be split')
        this.cancelUndoSnapshot()
        return
      }

      this.editingSheet.splitOffcutAllocation(alloc, splitDirectionType, splitDistance)

      this.commitUndoSnapshot()
      this.render()
      this.sendSheetLayoutUpdate()
    }
  }


  /**
   * Run an optimise process on the sheet's existing panels
   */
  optimiseSheet() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    this.sendSheetLayoutUpdate({
      clearAllPanelPackingData: true,
      resetViewport: true
    })
  }


  /**
   * Run auto-fill process on the sheet via the server
   */
  autoFillSheet() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    this.sendSheetLayoutUpdate({
      autoFillPanels: true,
      resetViewport: true
    })
  }


  /**
   * Add a panel to the sheet's pending list
   * Done with a pending list intermediate step as we currently don't run layout on the client. May change in the future
   * Done by editor user by dragging in a panel. Just adds to the list & be sent to the server for final layout
   */
  addPanelsToPendingList(panelList) {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    panelList.forEach(panel => {
      this.pendingPanels.push({
        id: panel.id
      })
    })

    this.sendSheetLayoutUpdate()
  }


  /**
   * Add a blank panel to the sheet's pending list
   * Done with a pending list intermediate step as we currently don't run layout on the client. May change in the future
   * Done by editor user by adding a new blank. Just adds to the list & be sent to the server for final layout
   */
  addBlankPanelToPendingList(blankPanelInfo) {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    this.pendingBlankPanels.push(blankPanelInfo)

    this.sendSheetLayoutUpdate()
  }


  /**
   * Clear the pending upload list
   * Done when submitting a layout request to the server
   */
  clearPendingList() {
    this.pendingPanels = []
    this.pendingBlankPanels = []
  }


  /**
   * Remove the selected allocations from the sheet
   * Done by editor user. Will remove from the list & be sent to the server for final layout
   */
  removeSheetSelectedAllocation() {
    // Ignore if not editing
    if(!this.isSheetEditingAvailable())
      return

    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    let selectedAllocs = this.getSelectedAllocations()

    this.editingSheet.optimiserSheetAllocations = this.editingSheet.optimiserSheetAllocations.filter(alloc => {
      if(selectedAllocs.find(selAlloc => selAlloc.id == alloc.id) == null)
        return true

      // Don't remove offcuts. We convert them to waste below & lock it in
      // Only do this if a single allocation selected. Ignored for selection boxes
      if(alloc.type == SheetAllocationTypes.offcut && selectedAllocs.length == 1)
        return true

      return false
    })

    // Handle removing offcuts
    this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
      if(alloc.type != SheetAllocationTypes.offcut)
        return

      // Convert the offcut to waste & lock it in place
      if(selectedAllocs.find(selAlloc => selAlloc.id == alloc.id) != null) {
        alloc.type = SheetAllocationTypes.waste
        alloc.isUserLocked = true

        // Convert the associated node to waste
        let node = this.editingSheet.findNodeById(alloc.packingNodeId)
        if(node) {
          node.type = OptimiserSheetNodeTypes.waste
          node.overflowTop = 0
          node.overflowRight = 0
          node.overflowBottom = 0
          node.overflowLeft = 0
        }
      }
    })

    this.sendSheetLayoutUpdate()
  }


  /**
   * Remove unlocked panels from the sheet
   */
  removeUnlockedPanels() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    this.sendSheetLayoutUpdate({
      clearUnlockedPanels: true,
      autoFillPanels: false
    })

    this.render()
  }


  /**
   * Remove all panels from the sheet
   */
  removeAllPanels() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    this.sendSheetLayoutUpdate({
      clearAllPanels: true,
      autoFillPanels: false,
      resetViewport: true
    })

    this.render()
  }


  /**
   * Reset all sheet allocations to defaults
   */
  resetSheetAllocations() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    // Clear all settings
    this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
      alloc.isUserLocked = false
      alloc.flippedHorz = false
      alloc.flippedVert = false
      alloc.isPanelSizeOverridden = false
      alloc.isPanelShapeCuttingDisabled = false
      alloc.reliefCutList = []
      alloc.cutoutList = []
    })

    // Update sheet with auto-fill
    this.sendSheetLayoutUpdate({
      autoFillPanels: true
    })

    this.render()
  }


  /**
   * Lock the sheet's selected allocation
   * Done by editor user. Will flag the allocation as locked, which will cause the sheet layout updating to use pre-packings with locked allocations
   */
  lockSheetSelectedAllocation() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    let selectedAllocs = this.getSelectedAllocations()
    selectedAllocs.forEach(alloc => {
      alloc.isUserLocked = true
    })

    this.sendSheetLayoutUpdate()
  }


  /**
   * Unlock the sheet's selected allocations
   * Done by editor user. Will clear locked flag & the allocation will no longer be included in the pre-packing
   */
  unlockSheetSelectedAllocation() {
    this.beginSheetUndoSnapshot()
    this.commitUndoSnapshot()

    let selectedAllocs = this.getSelectedAllocations()
    selectedAllocs.forEach(alloc => {
      alloc.isUserLocked = false
    })

    this.sendSheetLayoutUpdate()
  }


  /**
   * Attempt to rotate a sheet allocation (panel / offcut)
   * Forces a lock on the node & will clear nodes around if overlaps occur
   * 
   * Returns true if successful
   */
  rotateSheetAllocation(id, angle = 90) {
    let alloc = this.editingSheet.findAllocationById(id)
    if(alloc) {
      // TODO: Check angle limits (eg, grain)
      this.beginSheetUndoSnapshot()
      
      var allocOriginalRect = alloc.getRectangle()
      alloc.angle = MathsUtils.wrapAngleDegrees(alloc.angle + angle)

      if(angle == 90 || angle == -90) {
        let tmp = alloc.width
        alloc.width = alloc.height
        alloc.height = tmp
      }
      alloc.isUserLocked = true

      // Rotate & calculate required size
      let targetWidth = alloc.width
      let targetHeight = alloc.height
      let targetLeft = alloc.x
      let targetTop = alloc.y

      // Recalculate nodes for the target rectangle
      let position = { x: targetLeft + targetWidth/2, y: targetTop + targetHeight/2 }
      let resizeOptions = { nodeResized: true, oldAllocRect: allocOriginalRect }
      if(!this.editingSheet.resizeNodePositionInTree(alloc, alloc.packingNodeId, targetLeft, targetTop, targetWidth, targetHeight, alloc.angle, position, resizeOptions)) {

        this.editorComponent.errorMessage('No room to rotate', 'There is not enough room in the sheet or around the panel to rotate it')

        this.cancelUndoSnapshot()

        return false
      }

      this.commitUndoSnapshot()

      // Send updated tree node list to server to rebuild thumb/svg & remove lost allocs
      this.sendSheetLayoutUpdate()

      this.render()

      return true
    }

    return false
  }


  /**
   * Set the sheet allocation horizontal & vertical flipped state
   */
  setSheetAllocationFlipped(id, flipHorz, flipVert) {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.optimiserSheetAllocations.find(alloc => alloc.id == id)
    if(alloc) {
      alloc.isUserLocked = true
      alloc.flippedHorz = flipHorz
      alloc.flippedVert = flipVert

      this.commitUndoSnapshot()

      this.sendSheetLayoutUpdate()
    }
  }


  /**
   * Update the custom settings for a panel in a sheet allocation
   */
  updatePanelSheetAllocationInfo(id, data) {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.optimiserSheetAllocations.find(alloc => alloc.id == id)
    if(alloc) {
      if(alloc.type != SheetAllocationTypes.panel) {
        this.editorComponent.errorMessage('Edit error', 'Only panels can be edited')
        this.cancelUndoSnapshot()
        return
      }

      var allocOriginalRect = alloc.getRectangle()
      
      if(data.isPanelSizeOverridden) {
        alloc.setSizeOverridden(data.sourceWidth, data.sourceHeight)
      } else {
        // Restore the allocation size
        alloc.restoreSize()
      }

      // Other settings
      alloc.isPanelTemplateHandled = data.isPanelTemplateHandled
      alloc.isPanelCutoutsHandled = data.isPanelCutoutsHandled
      alloc.isPanelShapeCuttingDisabled = data.isPanelShapeCuttingDisabled

      // Force lock if custom settings set
      if(alloc.isPanelSizeOverridden || alloc.isPanelShapeCuttingDisabled)
        alloc.isUserLocked = true

      // Apply trim updates
      if(!data.isPanelSizeOverridden && !data.isPanelShapeCuttingDisabled && data.isPanelTrimOverridden) {
        alloc.setTrimOverridden(data.trimTop, data.trimRight, data.trimBottom, data.trimLeft)
      } else {
        // Restore trims
        alloc.restoreTrim()
      }

      // Clear all relief cuts when a panel is updated
      // Trim / size / overrides break existing relief cuts
      alloc.removeAllReliefCuts()

      // Resize into node
      let targetWidth = alloc.width
      let targetHeight = alloc.height
      let targetLeft = alloc.x
      let targetTop = alloc.y

      // Recalculate nodes for the target rectangle
      let position = { x: targetLeft + targetWidth/2, y: targetTop + targetHeight/2 }
      let resizeOptions = { nodeResized: true, oldAllocRect: allocOriginalRect, moveOtherAllocs: true }
      if(!this.editingSheet.resizeNodePositionInTree(alloc, alloc.packingNodeId, targetLeft, targetTop, targetWidth, targetHeight, alloc.angle, position, resizeOptions)) {

        this.editorComponent.errorMessage('No room to resize', 'There is not enough room in the sheet or around the panel to resize it')
        this.cancelUndoSnapshot()
        return
      }

      this.commitUndoSnapshot()
      this.render()
      this.sendSheetLayoutUpdate()
    }
  }

  /**
   * Extend the selected edge outwards as much as possible
   */
  extendSelectedEdge() {
    this.beginSheetUndoSnapshot()

    try {
      if(!this.editingSheet.extendCuttingLine(this.editingSheet.findNodeById(this.selectedInfo.nodeId), this.selectedInfo.edgeType)) {
        this.editorComponent.errorMessage('Cannot extend edge', 'Unable to extend edge due to neighbouring panels')
        this.cancelUndoSnapshot()
        return
      }
    } catch(err) {
      this.editorComponent.errorMessage('Cannot extend edge', 'Unable to extend edge due to neighbouring panels')
      this.cancelUndoSnapshot()
      return
    }

    this.commitUndoSnapshot()
    this.render()
    this.sendSheetLayoutUpdate()
  }


  /**
   * Add a relief cutting line to the selected cutting line
   */
  addReliefCutToSelectedCuttingLine(axisType) {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(!this.editingSheet.addReliefCut(alloc, axisType, this.selectedInfo.point, this.selectedInfo.segment)) {
      this.editorComponent.errorMessage('Cannot add relief cut', 'Unable to add a relief cut to the selected cutting line')
      this.cancelUndoSnapshot()
    }

    this.commitUndoSnapshot()
    this.render()
    this.sendSheetLayoutUpdate()
  }


  /**
   * Remove the selected relief cut
   */
  removeSelectedReliefCut() {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(!this.editingSheet.removeReliefCut(alloc, this.selectedInfo.reliefCutIndex)) {
      this.editorComponent.errorMessage('Cannot remove relief cut', 'Unable to remove the relief cut')
      this.cancelUndoSnapshot()
    }

    this.commitUndoSnapshot()
    this.render()
    this.sendSheetLayoutUpdate()
  }


  /**
   * Remove all the relief cuts from the selected relief cut's allocation
   */
  removeSelectedAllocAllReliefCut() {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(!this.editingSheet.removeAllReliefCuts(alloc)) {
      this.editorComponent.errorMessage('Cannot remove relief cut', 'Unable to remove the relief cut')
      this.cancelUndoSnapshot()
    }

    this.commitUndoSnapshot()
    this.render()
    this.sendSheetLayoutUpdate()
  }


  /**
   * Adds the selected cutout to the cutting list for the allocation
   * Runs a query on the panel to get the new trim required for the cutout in the allocation
   */
  addSelectedCutoutToAllocation() {
    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(alloc) {

      // Run a query on the server to get required trim info for the cutout list
      this.requestCalculatePanelTrimData(alloc.productionPanelId, _.concat(alloc.cutoutList, [{ index: this.selectedInfo.cutoutIndex }]))
      .then(panelTrimInfo => {

        this.beginSheetUndoSnapshot()
        if(!this.editingSheet.addCutout(alloc, this.selectedInfo.cutoutIndex, { trimTop: panelTrimInfo.trimTop, trimRight: panelTrimInfo.trimRight, trimBottom: panelTrimInfo.trimBottom, trimLeft: panelTrimInfo.trimLeft })) {

          this.editorComponent.errorMessage('Could not add cutout', 'There is not enough room in the sheet or around the panel to resize it for the required cutout trim')

          this.cancelUndoSnapshot()
          return
        }

        this.commitUndoSnapshot()
        this.render()
        this.sendSheetLayoutUpdate()
      })
      .catch(err => {
        console.error(err)
        this.editorComponent.displayNetworkError(err, 'Failed to update allocation')
      })
    }
  }


  /**
   * Removes the selected cutout from the cutting list for the allocation
   */
  removeSelectedCutoutFromAllocation() {
    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(alloc) {
      var cutoutList = alloc.cutoutList.filter(cutoutInfo => cutoutInfo.index != this.selectedInfo.cutoutIndex)

      // Run a query on the server to get required trim info for the cutout list
      this.requestCalculatePanelTrimData(alloc.productionPanelId, cutoutList)
      .then(panelTrimInfo => {

        this.beginSheetUndoSnapshot()
        if(!this.editingSheet.removeCutout(alloc, this.selectedInfo.cutoutIndex, { trimTop: panelTrimInfo.trimTop, trimRight: panelTrimInfo.trimRight, trimBottom: panelTrimInfo.trimBottom, trimLeft: panelTrimInfo.trimLeft })) {

          this.editorComponent.errorMessage('Could not remove cutout', 'There is not enough room in the sheet or around the panel to resize it for the required cutout trim')

          this.cancelUndoSnapshot()
          return
        }

        this.commitUndoSnapshot()
        this.render()
        this.sendSheetLayoutUpdate()
      })
      .catch(err => {
        console.error(err)
        this.editorComponent.displayNetworkError(err, 'Failed to update allocation')
      })
    }
  }


  /**
   * Get the status of the selected cutout on the linked allocation to see if it's flagged for cutting at optimisation or not
   */
  isSelectedCutoutFlagged() {
    let alloc = this.editingSheet.findAllocationById(this.selectedInfo.allocationId)
    if(alloc)
      return alloc.isCutoutFlagged(this.selectedInfo.cutoutIndex)

    return false
  }



  /**
   * Convert a waste allocation into an offcut
   */
  convertAllocationToOffcut(id) {
    this.beginSheetUndoSnapshot()

    let alloc = this.editingSheet.findAllocationById(id)
    if(!alloc) {
      this.editorComponent.errorMessage('Allocation not found')
      this.cancelUndoSnapshot()
      return
    }
    if(alloc.type != SheetAllocationTypes.waste) {
      this.editorComponent.errorMessage('Allocation is not waste', 'Can only convert waste allocations to offcuts')
      this.cancelUndoSnapshot()
      return
    }

    alloc.type = SheetAllocationTypes.offcut
    alloc.isUserLocked = true
    alloc.overflowTop = 0
    alloc.overflowRight = 0
    alloc.overflowBottom = 0
    alloc.overflowLeft = 0

    // Convert the node to an offcut for sending pre-packings
    let node = this.editingSheet.findNodeById(alloc.packingNodeId)
    if(node) {
      node.type = OptimiserSheetNodeTypes.offcut
    }

    this.commitUndoSnapshot()
    this.render()
    this.sendSheetLayoutUpdate()
  }


  /**
   * Calculate the list of defects in the sheet allocations & return the list
   * Used for updating layout
   */
  getDefectList() {
    return this.editingSheet.optimiserSheetAllocations
      .filter(alloc => alloc.type == SheetAllocationTypes.defect)
      .map(alloc => {
        return {
          id: alloc.id,
          x: alloc.x,
          y: alloc.y,
          width: alloc.width,
          height: alloc.height
        }
      })
  }


  /**
   * Calculate the list of locked panels
   * Requires as pre-packed panels still need to link to the panel in the source list
   * Used for updating layout
   */
  getLockedPanelList() {
    return this.editingSheet.optimiserSheetAllocations
      .filter(alloc => {
        if(alloc.type == SheetAllocationTypes.panel)
          return true
        if(alloc.type == SheetAllocationTypes.panelScratchPad)
          return true
        return false
      })
      .map(alloc => {
        return {
          id: alloc.productionPanelId
        }
      })
  }


  /**
   * Calculate the list of locked blank panels
   * Required as pre-packed blank panels still need a link to the blank panel in the source list
   * Used for updating layout
   */
  getLockedBlankPanelList() {
    return this.editingSheet.optimiserSheetAllocations
      .filter(alloc => {
        if(alloc.type == SheetAllocationTypes.blankPanel)
          return true
        return false
      })
      .map(alloc => {
        return {
          blankPanelUUID: alloc.blankPanelUUID,
          userInfo: alloc.userInfo,
          width: alloc.sourceWidth,
          height: alloc.sourceHeight,
        }
      })
  }


  /**
   * Get the list of pending panels awaiting layout for the sheet allocations
   * Used for updating layout
   */
  getPendingPanelList() {
    return this.pendingPanels
  }


  /**
   * Get the list of pending blank panels awaiting layout for the sheet allocations
   * Used for updating layout
   */
  getPendingBlankPanelList() {
    return this.pendingBlankPanels
  }


  /**
   * Builds the list of panel configuration data that goes along with locked panels in the sheet
   * Loose / pending panels are ignored
   */
  buildPanelConfigList() {
    return this.editingSheet.optimiserSheetAllocations
      .filter(alloc => {
        if(alloc.type == SheetAllocationTypes.panel)
          return true
        if(alloc.type == SheetAllocationTypes.panelScratchPad)
          return true

        return false
      })
      .map(alloc => {
        return {
          productionPanelId: alloc.productionPanelId,
          isUserLocked: true,
          isPanelSizeOverridden: alloc.isPanelSizeOverridden,
          isPanelShapeCuttingDisabled: alloc.isPanelShapeCuttingDisabled,
          isPanelTrimOverridden: alloc.isPanelTrimOverridden,
          isPanelTemplateHandled: alloc.isPanelTemplateHandled,
          isPanelCutoutsHandled: alloc.isPanelCutoutsHandled,
          sourceWidth: alloc.sourceWidth,
          sourceHeight: alloc.sourceHeight,
          x: alloc.x,
          y: alloc.y,
          width: alloc.width,
          height: alloc.height,
          angle: alloc.angle,
          flippedHorz: alloc.flippedHorz,
          flippedVert: alloc.flippedVert,
          trimTop: alloc.trimTop,
          trimRight: alloc.trimRight,
          trimBottom: alloc.trimBottom,
          trimLeft: alloc.trimLeft,
          isPanelScratchPad: alloc.type == SheetAllocationTypes.panelScratchPad,
          reliefCutList: alloc.reliefCutList.map(reliefCut => {
            return {
              startPosition: reliefCut.startPosition,
              endPosition: reliefCut.endPosition
            }
          }),
          cutoutList: alloc.cutoutList.map(cutout => {
            return {
              index: cutout.index
            }
          })
        }
      })
  }


  /**
   * Build the pre packing data object for sending to the server
   * Contains the guillotine binary tree with nodes
   * Used for updating layout
   * 
   * Only locked in place panels/offcuts/waste/blankPanels will be included
   * If no panels/offcuts/waste/blanks are locked, returns an empty tree
   * 
   */
  buildPrePackingData() {
    let lockedAllocs = this.editingSheet.optimiserSheetAllocations.filter(alloc => {
      if(alloc.type == SheetAllocationTypes.panel || alloc.type == SheetAllocationTypes.defect)
        return true
      if(alloc.type == SheetAllocationTypes.offcut && alloc.isUserLocked)
        return true
      if(alloc.type == SheetAllocationTypes.waste && alloc.isUserLocked)
        return true
      if(alloc.type == SheetAllocationTypes.blankPanel)
        return true

      return false
    })
    
    // Remove any invalid panel allocations (ie, cancelled panels) from the list
    lockedAllocs = lockedAllocs.filter(alloc => {
      if(alloc.type == SheetAllocationTypes.panel) {
        if(alloc.productionPanel.status == ProductionPanelStatusTypes.cancelled || alloc.order.status == OrderStatusTypes.cancelled)
          return false
      }

      return true
    })    

    if(lockedAllocs.length == 0)
      return {}

    // Traverse each node down. If no allocations are at the leafs of a node, stop traversing
    if(!this.editingSheet._nodeLeafsContainsAllocs(this.editingSheet.rootNode, lockedAllocs))
      return {}

    let rootNode = this._processPrePackedNode(this.editingSheet.rootNode, lockedAllocs);

    return { rootNode: rootNode }
  }


  /**
   * Process a pre-packed node in the guillotine binary tree
   * If no allocations are in this node or any end leafs, don't process this node's children
   */
  _processPrePackedNode(node, lockedAllocsList) {
    let outputNode = {
      type: node.type,
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
      splitDir: node.splitDir,
      userData: {},
      childNodes: []
    }

    // Convert all waste/offcut nodes to regular nodes available for packing unless they are locked
    if(node.type == OptimiserSheetNodeTypes.waste || node.type == OptimiserSheetNodeTypes.offcut) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(node.type == OptimiserSheetNodeTypes.offcut && alloc.type == SheetAllocationTypes.offcut)
          return (alloc.packingNodeId == node.id)
        else if(node.type == OptimiserSheetNodeTypes.waste && alloc.type == SheetAllocationTypes.waste)
          return (alloc.packingNodeId == node.id)
        return false
      })

      // Only set info if locked alloc found
      // Set user locked flag for the server to handle in the allocations list
      // Otherwise set as a node for re-allocation
      if(foundAlloc) {
        outputNode.userData.isUserLocked = true
      }
      else {
        outputNode.type = OptimiserSheetNodeTypes.node
      }
    }
    else if(node.type == OptimiserSheetNodeTypes.panel) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.panel)
          return (alloc.productionPanelId == node.panelId)
        if(alloc.type == SheetAllocationTypes.blankPanel)
          return (alloc.packingNodeId == node.id)
        return false
      })

      if(foundAlloc) {
        outputNode.angle = foundAlloc.angle

        if(foundAlloc.type == SheetAllocationTypes.panel) {
          outputNode.panelId = node.panelId
          outputNode.flippedHorz = foundAlloc.flippedHorz
          outputNode.flippedVert = foundAlloc.flippedVert
        } else if(foundAlloc.type == SheetAllocationTypes.blankPanel) {
          outputNode.isBlankPanel = true
          outputNode.panelId = node.panelId
        }
      } else {
        outputNode.type = OptimiserSheetNodeTypes.node
      }
    }
    else if(node.type == OptimiserSheetNodeTypes.defect) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.defect)
          return (alloc.packingNodeId == node.id)
        return false
      })

      if(!foundAlloc) {
        outputNode.type = OptimiserSheetNodeTypes.node
      }
    }

    // Children
    if(node.childNodes && node.childNodes.length == 2) {
      if(this.editingSheet._nodeLeafsContainsAllocs(node.childNodes[0], lockedAllocsList) || this.editingSheet._nodeLeafsContainsAllocs(node.childNodes[1], lockedAllocsList)) {
        outputNode.childNodes.push(this._processPrePackedNode(node.childNodes[0], lockedAllocsList))
        outputNode.childNodes.push(this._processPrePackedNode(node.childNodes[1], lockedAllocsList))
      }
    }

    return outputNode
  }


  /**
   * Clear the undo list
   * Done if changing between sheets in the editor
   */
  clearUndoList() {
    this.undoSheetList = []
    this.currentUndoSheetSnapshot = null
  }


  /**
   * Begin a snapshot of the current sheet ready to be put into the undo list
   * Commiting the snapshot will move it to the undo list
   * Cancelled the snapshot will clear the snapshot taken
   * 
   * Note: We just snapshot the entire sheet rather than store actions/command lists to undo for simplicity
   */
  beginSheetUndoSnapshot() {
    if(this.editingSheet) {
      this.currentUndoSheetSnapshot = this.editingSheet.clone()
    }
  }


  /**
   * Commit a snapshot taken by beginSheetUndoSnapshot()
   */
  commitUndoSnapshot() {
    if(!this.currentUndoSheetSnapshot) {
      console.error('No undo snapshot taken')
      return
    }

    this.undoSheetList.push(this.currentUndoSheetSnapshot)
    this.currentUndoSheetSnapshot = null

    // Trim the old undo groups out to save memory
    if(this.undoSheetList.length > this.maxUndoHistory)
      this.undoSheetList.splice(0, 1)
  }


  /**
   * Cancel a snapshot taken
   */
  cancelUndoSnapshot() {
    if(this.currentUndoSheetSnapshot) {
      this.editingSheet.copy(this.currentUndoSheetSnapshot)
      this._updateSheetScale()
    }

    this.currentUndoSheetSnapshot = null
    this.render()
  }


  /**
   * Undo the last action performed
   */
  undoLastAction() {
    if(this.editingSheet && this.undoSheetList.length > 0) {

      var lastUndoSheet = this.undoSheetList.pop()
      this.editingSheet.copy(lastUndoSheet)      

      // Clear pending list
      this.pendingPanels = []
      this.pendingBlankPanels = []

      // Update info
      this.info.optimiserSheetId = this.editingSheet.id
      this._updateSheetScale()
      this.render()

      // Send updated tree node list to server
      this.sendSheetLayoutUpdate()
    }
  }


  /**
   * Check if editing is available for the sheet
   */
  isSheetEditingAvailable() {
    if(!this.editingSheet)
      return false

    if(this.editingSheet.type == OptimiserSheetTypes.splitSheet)
      return false    

    if(this.editingSheet.status == OptimiserSheetStatusTypes.cutting ||
       this.editingSheet.status == OptimiserSheetStatusTypes.completed ||
       this.editingSheet.status == OptimiserSheetStatusTypes.completed)
      return false
      
    return true
  }


  /**
   * Check if the current sheet contains an allocation linked to an order
   */
  isSheetContainOrderPanel(orderId) {
    if(!this.editingSheet)
      return false

    var orderPanel = this.editingSheet.optimiserSheetAllocations.find(alloc => {
      return alloc.isPanel() && alloc.orderId == orderId
    })
    
    return orderPanel != null
  }


  /**
   * Request a panel's details from the server
   * Used when dragging a new panel into the sheet & we require the trim details for accurate sizing & shape info for render
   */
  requestPanelOptimiserDetails(productionPanelId) {
    this.editorComponent.$apiService.glassProductionStationService.getOptimiserPanelDetails(this.productionStationId, productionPanelId)
    .then(response => {
      // If the grabbed panel is not valid or is a different id, ignore this request
      // Happens if the request times out & the user tries again
      let panelDetails = response.data
      if(this.grabbedAlloc.panelDownloading && this.grabbedAlloc.productionPanelId == panelDetails.id) {
        this.grabbedAlloc.panelDownloading = false
        this.grabbedAlloc.panelDownloadFailed = false

        // Fill in info
        this.grabbedAlloc.sourceWidth = panelDetails.allocationWidth
        this.grabbedAlloc.sourceHeight = panelDetails.allocationHeight

        // Fill in shapeData for rendering
        this.grabbedAlloc.panelDownloadInfo = {
          isEditorGeneratedItem: panelDetails.isEditorGeneratedItem,
          shapeData: _.cloneDeep(panelDetails.shapeData || {}),
          trimTop: panelDetails.trimTop,
          trimRight: panelDetails.trimRight,
          trimBottom: panelDetails.trimBottom,
          trimLeft: panelDetails.trimLeft,
          defaultCutoutList: panelDetails.defaultCutoutList
        }

        this.renderMoveGrabbedAllocation()
      }
    })
    .catch(err => {
      // Error
      // Flag grabbed alloc as failed download somehow so it doesn't get inserted
      console.error(err)
      
      if(this.grabbedAlloc.panelDownloading && this.grabbedAlloc.productionPanelId == productionPanelId) {
        this.grabbedAlloc.panelDownloading = false
        this.grabbedAlloc.panelDownloadFailed = true
      }
    })
  }


  /**
   * Request the trim info for a panel with cutouts from the server
   * Used when adding/removing cutouts from a panel allocation on the sheet that may require trim changes & therefore allocation resizing
   * 
   * Returns a promise resolved with the query results
   */
  requestCalculatePanelTrimData(productionPanelId, flaggedCutoutList) {
    return new Promise(async (resolve, reject) => {
      var calcData = {
        cutouts: flaggedCutoutList || []
      }

      this.editorComponent.updatingServer = true
      this.editorComponent.$apiService.glassProductionStationService.calculateOptimiserPanelTrim(this.productionStationId, productionPanelId, calcData)
      .then(response => {
        
        this.editorComponent.updatingServer = false
        return resolve(response.data)
      })
      .catch(err => {
        console.error(err)

        this.editorComponent.updatingServer = false
        
        return reject(err)
      })
    })
  }


  /**
   * Calculate a list of warnings based on sheet issues / config rule conflicts on the sheet
   */
  calculateSheetWarningList() {
    if(!this.editingSheet)
      return []
    var warningList = []

    // 1) Check for any waste allocations that are smaller than the minimum panel trim allowed
    this.editingSheet.optimiserSheetAllocations.forEach(alloc => {
      if(alloc.type == SheetAllocationTypes.waste) {

        let minTrim = this.editingSheetConfig.panelTrim || 0
        if(alloc.width < minTrim || alloc.height < minTrim) {
          warningList.push({
            type: 'waste',
            sheetAllocationId: alloc.id,
            message: `Waste allocation [${alloc.height}mm x ${alloc.width}mm] is smaller than the minimum panel trim of ${minTrim}mm`
          })
        }
      }
    })

    return warningList
  }


  /**
   * Calculate the zoom step index given a scale
   */
  calculateZoomStepFromScale(scale) {
    scale = MathsUtils.clampValue(scale, this.settings.zoom.minZoom, this.settings.zoom.maxZoom)

    let fraction = (Math.log(scale) - Math.log(this.settings.zoom.minZoom)) / (Math.log(this.settings.zoom.maxZoom) - Math.log(this.settings.zoom.minZoom))
    return MathsUtils.lerp(0, this.settings.zoom.zoomStepCount, fraction)
  }

  /**
   * Clamp the camera view origin to not move the sheet offscreen
   */
  clampCameraPosition() {
    this.info.originOffsetX = Math.min(this.info.originOffsetX, 0)
    this.info.originOffsetY = Math.min(this.info.originOffsetY, 0)

    // Make sure we show part of the sheet & don't completely move it offscreen
    var minRequiredVisible = 200;   // Screen Pixels
    var maxX = (this.editingSheet.width * this.info.scale) - minRequiredVisible;
    var maxY = (this.editingSheet.height * this.info.scale) - minRequiredVisible;

    this.info.originOffsetX = Math.max(this.info.originOffsetX, -maxX)
    this.info.originOffsetY = Math.max(this.info.originOffsetY, -maxY)
  }


  /**
   * Reset the zoom / scale / origin info
   */
  resetZoom() {
    this._updateSheetScale()
    this.info.originOffsetX = 0
    this.info.originOffsetY = 0
  }


}